import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatApiError, formatSeconds, formatNumber, formatBytes, formatDateTime } from '../../utils/formatters';
import axios from 'axios';
import { FiX, FiRefreshCw, FiPlay, FiExternalLink, FiClock, FiCpu, FiInfo, FiAlertTriangle, FiEye, FiEyeOff, FiTrendingUp, FiFilter, FiSearch, FiBarChart2, FiLayers, FiMonitor } from 'react-icons/fi';
import { QueryViewButton, QueryViewDialog } from './QueryView';
import useSqlPlanViewer from './useSqlPlanViewer';
import PanelChart from './PanelChart';
import ObjectReadsList from './ObjectReadsList';
import './MonitoringAnalysisModal.css';
import AppModal from '../common/AppModal';

// 실행 이력 입력 — 이 모양이면 SQL_ID, 아니면 SQL 텍스트로 찾는다(2026-10-22). Oracle SQL_ID 는 13자리 영숫자다
const SQL_ID_SHAPE = /^[0-9a-z]{13}$/i;
const SQL_SEARCH_MIN = 3;

// 실행 중인 쿼리 조회 — 기존 /sql-developer/execute 를 그대로 재사용 (DynamicViewExplorerModal 과 동일 패턴).
// **목록 SQL 은 여기에 없다.** 백엔드(`ashCommon.js` + `/active-queries`)가 만든다.
//
// 예전에는 이 파일이 상수 SQL 을 들고 /execute 로 쏘았다. 그래서 텍스트 필터가
// "50건 받아 온 뒤 매칭" 이었고(검색이 검색이 아니었다), `%IDS%` 를 문자열로 조립했고,
// 실행 중 판별을 `last_seen` 으로 추측했다. 셋 다 서버에서만 제대로 풀린다.
// 「조회 쿼리 보기」도 사본이 아니라 `/source-sql` 이 내려주는 **실제 SQL** 을 보여 준다.


// 수집 결과 그리드의 컬럼은 서로 다른 동적뷰에서 온다. 소스 단위로 켜고 끌 수 있도록
// (컬럼 → 소스) 매핑을 한곳에 둔다. ID/Operation 은 행 식별자라 항상 표시.
const getPlanSources = (t) => [
    {
        key: 'xplan',
        label: 'DBMS_XPLAN',
        title: t('monitoring:anal.displayCursor'),
    },
    {
        key: 'monitor',
        label: 'GV$SQL_PLAN_MONITOR',
        title: t('monitoring:anal.perLineStats'),
    },
    {
        key: 'ash',
        label: 'GV$ACTIVE_SESSION_HISTORY',
        title: t('monitoring:anal.perLineWaits'),
    },
    {
        key: 'workarea',
        label: 'GV$SQL_WORKAREA_ACTIVE',
        title: t('monitoring:anal.workarea'),
    },
];

/**
 * 실행계획 그리드의 칸 — **이름이 그 값의 정체를 말한다.**
 *
 * 예전에는 한 칸(「소요시간」)에 xplan A-Time·plan monitor span·ASH 샘플이 폴백으로 섞였고,
 * 「물리읽기」에는 블록 수와 요청 건수가 섞였다(요청 1건 = 최대 128블록). 출처를 `source` 로
 * 적어 두면서 실제로는 그 값이 아닌 경우가 많아, 소스 칩이 거짓말을 했다(신고 2026-09-21).
 *
 * 이제 칸마다 소스가 하나다. 특히:
 *  - **DB Time** 은 활성세션 기준이라 병렬도(DOP)만큼 커진다 — 「소요시간」이라 부르면 거짓이다.
 *  - **경과**는 벽시계다. 둘을 나란히 두면 "DB Time 48s / 경과 6s / DOP 8" 이 한눈에 읽힌다.
 *  - **캐시읽기**는 그 커서의 **최종 실행** 값이라 지금 보는 실행의 것이라는 보장이 없다.
 *    보장을 만들 방법이 없으므로 라벨이 늘 그 사실을 말한다.
 *
 * source: null 이면 항상 표시. numeric: true 면 오른쪽 정렬(+ 자릿수 정렬용 tabular-nums).
 */
const getPlanColumns = (t) => [
    { key: 'id', label: 'ID', source: null },
    { key: 'operation', label: 'Operation', source: null },
    // 오브젝트 이름은 plan monitor 를 우선한다(그쪽만 실행 필터를 탄다)
    { key: 'object', label: 'Object', source: 'monitor' },
    { key: 'dbTime', label: t('monitoring:anal.col.dbTime'), source: 'ash', numeric: true,
      hint: t('monitoring:anal.col.dbTimeHint') },
    { key: 'elapsed', label: t('monitoring:anal.col.wallClock'), source: 'monitor', numeric: true,
      hint: t('monitoring:anal.col.wallClockHint') },
    { key: 'rows', label: 'Rows', source: 'monitor', numeric: true },
    { key: 'readBytes', label: t('monitoring:anal.col.ioBytes'), source: 'monitor', numeric: true,
      hint: t('monitoring:anal.col.ioBytesHint') },
    { key: 'physicalReads', label: t('monitoring:anal.col.readReq'), source: 'monitor', numeric: true,
      hint: t('monitoring:anal.col.readReqHint') },
    { key: 'cacheReads', label: t('monitoring:anal.col.buffersLast'), source: 'xplan', numeric: true,
      hint: t('monitoring:anal.col.buffersLastHint') },
    { key: 'waitEvent', label: t('monitoring:anal.col.waitEvent'), source: 'ash' },
    { key: 'memory', label: t('monitoring:anal.col.memory'), source: 'workarea', numeric: true },
    { key: 'temp', label: t('monitoring:anal.col.temp'), source: 'workarea', numeric: true },
];

// key 목록은 번역과 무관하다(title 만 번역됨). 초기 state 용이라 모듈 스코프에 둔다.
const PLAN_SOURCE_KEYS = ['xplan', 'monitor', 'ash', 'workarea'];
const ALL_SOURCES_ON = PLAN_SOURCE_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {});

const getIntervalOptions = (t) => [
    { value: 10, label: t('monitoring:anal.range.sec10') },
    { value: 30, label: t('monitoring:anal.range.sec30') },
    { value: 60, label: t('monitoring:anal.range.min1') },
    { value: 300, label: t('monitoring:anal.range.min5') },
    { value: 600, label: t('monitoring:anal.range.min10') },
];

const API_ROOT = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/sql-developer\/?$/, '');
// 「실행 중인 쿼리」 목록의 컬럼. **한 곳에서 정의하고 thead·colSpan 이 여기서 파생된다** —
// 예전에는 열 수 6 이 세 군데 박혀 있어 컬럼을 늘릴 때마다 표가 어긋났다.
// `order` 가 있는 컬럼은 머리글을 눌러 그 값으로 정렬한다 — 정렬 선택 상자와 같은 별칭(서버 ashCommon.ORDER_EXPR)이다.
// 정렬 기준 값이 전부 컬럼으로 보여야 「왜 이 순서인가」 를 표가 말한다(2026-10-14).
const LIVE_ORDERS = ['recent_elapsed', 'samples', 'avg_elapsed', 'exec_count', 'cpu', 'lock'];
const getLiveColumns = (t) => [
    { key: 'sqlId', label: 'SQL_ID' },
    { key: 'sqlText', label: t('monitoring:anal.sqlText') },
    { key: 'state', label: t('monitoring:anal.col.state') },
    // 이 SQL 이 돈 노드(RAC) — 하나면 번호, 여럿이면 목록·범위
    { key: 'inst', label: t('monitoring:anal.col.inst'), hint: t('monitoring:anal.col.instHint') },
    { key: 'recentElapsed', label: t('monitoring:anal.order.recent_elapsed'), hint: t('monitoring:anal.orderHint.recent_elapsed'), order: 'recent_elapsed', num: true },
    { key: 'samples', label: t('monitoring:anal.order.samples'), hint: t('monitoring:anal.orderHint.samples'), order: 'samples', num: true },
    { key: 'avgElapsed', label: t('monitoring:anal.order.avg_elapsed'), hint: t('monitoring:anal.orderHint.avg_elapsed'), order: 'avg_elapsed', num: true },
    { key: 'execCount', label: t('monitoring:anal.order.exec_count'), hint: t('monitoring:anal.orderHint.exec_count'), order: 'exec_count', num: true },
    { key: 'cpu', label: t('monitoring:anal.order.cpu'), hint: t('monitoring:anal.orderHint.cpu'), order: 'cpu', num: true },
    { key: 'lock', label: t('monitoring:anal.order.lock'), hint: t('monitoring:anal.orderHint.lock'), order: 'lock', num: true },
    { key: 'sessionCount', label: t('monitoring:anal.col.session'), order: 'sessions', num: true },
    { key: 'module', label: t('monitoring:anal.col.module') },
    { key: 'lastSeen', label: t('monitoring:anal.col.lastSeen') },
    { key: 'actions', label: '' },
];

// 실행 이력(AWR) 컬럼 — **세 무리**로 나눈다.
//
// 나누는 이유: 이 표의 수치는 대부분 **구간 합계(Δ)** 인데 그게 안 보여서 "실행당 값인 줄
// 알았다" 는 오해가 났다. 머리글을 두 단으로 만들어 무엇이 합계이고 무엇이 실행당인지
// 표가 직접 말하게 한다. 무리 이름·열 순서가 여기 한 곳에서 나온다.
const HISTORY_GROUPS = ['id', 'delta', 'perExec'];
// 소요시간 그래프의 구성 — 쌓으면 실행당 소요시간이 된다. 기타 WAIT 는 「소요 − CPU − 네 대기」(음수는 0 — 반올림 차이)
const HISTORY_TIME_PARTS = ['cpu', 'ioWait', 'clWait', 'apWait', 'ccWait', 'otherWait'];
const HISTORY_PART_COLORS = { cpu: '#22c55e', ioWait: '#3b82f6', clWait: '#a855f7', apWait: '#ef4444', ccWait: '#f59e0b', otherWait: '#94a3b8' };
const historyChartPoint = (h) => {
    const per = h.elapsedPerExecSec;
    const parts = {
        cpu: h.cpuPerExecSec, ioWait: h.ioWaitPerExecSec, clWait: h.clWaitPerExecSec,
        apWait: h.apWaitPerExecSec, ccWait: h.ccWaitPerExecSec,
    };
    const known = Object.values(parts).reduce((a, v) => a + (v || 0), 0);
    return {
        ...parts,
        otherWait: per == null ? null : Math.max(0, Math.round((per - known) * 10000) / 10000),
        executions: h.executions,
        bufferGetsPerExec: h.bufferGetsPerExec,
        diskReadsPerExec: h.diskReadsPerExec,
    };
};
const getHistoryColumns = (t) => [
    { group: 'id', key: 'snapId', label: 'SNAP_ID', mono: true },
    { group: 'id', key: 'beginTime', label: t('monitoring:anal.col.windowStart') },
    { group: 'id', key: 'instanceNumber', label: 'INST' },
    { group: 'id', key: 'planHashValue', label: 'PLAN_HASH', mono: true },
    { group: 'delta', key: 'executions', label: t('monitoring:anal.col.execCount'), fmt: 'int' },
    { group: 'delta', key: 'elapsedSec', label: t('monitoring:anal.col.elapsed'), fmt: 'sec', rel: 'time' },
    { group: 'delta', key: 'cpuSec', label: 'CPU', fmt: 'sec', rel: 'time' },
    { group: 'delta', key: 'ioWaitSec', label: t('monitoring:anal.col.ioWait'), fmt: 'sec', rel: 'time' },
    { group: 'delta', key: 'bufferGets', label: 'Buffer Gets', fmt: 'int', rel: 'io' },
    { group: 'delta', key: 'diskReads', label: 'Disk Reads', fmt: 'int', rel: 'io' },
    { group: 'delta', key: 'rowsProcessed', label: t('monitoring:anal.col.rows'), fmt: 'int' },
    { group: 'perExec', key: 'elapsedPerExecSec', label: t('monitoring:anal.col.elapsed'), fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'cpuPerExecSec', label: 'CPU', fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'ioWaitPerExecSec', label: 'IOWAIT', fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'clWaitPerExecSec', label: 'CLWAIT', fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'apWaitPerExecSec', label: 'APWAIT', fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'ccWaitPerExecSec', label: 'CCWAIT', fmt: 'sec', rel: 'time' },
    { group: 'perExec', key: 'bufferGetsPerExec', label: 'Buffer Gets', fmt: 'int', rel: 'io' },
    { group: 'perExec', key: 'diskReadsPerExec', label: 'Disk Reads', fmt: 'int', rel: 'io' },
];

const API_BASE = `${API_ROOT}/sql-developer`;
const PERF_API = `${API_BASE}/monitoring/sql-perf`;
const DBMS_PERF_API = `${API_BASE}/monitoring/dbms-perf`;
const getDimensionLabel = (t) => ({ sql: 'SQL', session: t('monitoring:anal.col.session'), module: t('monitoring:anal.col.module'), wait_event: t('monitoring:anal.col.waitEvent') });

// 표시 포맷은 utils/formatters 로 위임한다 — 현재 언어에 맞는 로케일을 쓰므로
// 영어로 바꾸면 숫자·날짜 표기도 따라온다(예전에는 ko-KR 이 박혀 있었다).
// ASH 는 1초 표본이라 첫 표본은 실행 시작 몇 초 뒤에 찍힌다 — 그만큼은 「초반이 잘렸다」 가 아니다(1초 차이로 경고가 뜨던 오탐, 2026-10-14)
const ASH_TRUNC_TOLERANCE_SEC = 10;
const ashTruncatedSec = (start, coveredFrom) => {
    const a = Date.parse(String(start).replace(' ', 'T'));
    const b = Date.parse(String(coveredFrom).replace(' ', 'T'));
    return Number.isFinite(a) && Number.isFinite(b) ? (b - a) / 1000 : 0;
};
const fmtSec = (v) => formatSeconds(v);
// 실행 중인 쿼리의 노드 — 하나면 번호, 둘이면 「1,2」, 그 이상은 범위와 수
const instLabel = (row) => {
    if (row.instMin == null) return '-';
    if (!row.instCnt || row.instCnt <= 1 || row.instMax === row.instMin) return String(row.instMin);
    if (row.instCnt === 2 && row.instMax - row.instMin === 1) return `${row.instMin},${row.instMax}`;
    return `${row.instMin}~${row.instMax} (${row.instCnt})`;
};
const fmtSecOrDash = (v) => (v == null || v === '-' ? '-' : formatSeconds(v));
// 실행 상태 — 실행 중 / 페치 대기 / 판정 못 함 / 끝남. 목록은 이 순서로 먼저 가른다(2026-11-03 시험 피드백: 실행 중인 것이 섞여 있었다)
const liveStateOf = (row) => (row.isDemo ? 'unknown' : row.isRunning === true ? 'run' : row.fetchWait ? 'fetch' : row.isRunning === false ? 'done' : 'unknown');
const LIVE_STATE_RANK = { run: 0, fetch: 1, unknown: 2, done: 3 };
// 정렬 기준(서버 ashCommon.ORDER_EXPR 의 별칭) → 행의 값. 기준을 바꾸면 화면에서 곧바로 다시 세우고, 서버에도 다시 묻는다
const LIVE_ORDER_VALUE = {
    recent_elapsed: r => r.recentElapsedSec, samples: r => r.samples, avg_elapsed: r => r.avgElapsedSec,
    exec_count: r => r.execCount, cpu: r => r.cpuSamples, lock: r => r.lockSamples, sessions: r => r.sessionCount,
    last_seen: r => (r.lastSeen ? Date.parse(String(r.lastSeen).replace(' ', 'T')) : null),
    first_seen: r => (r.firstSeen ? Date.parse(String(r.firstSeen).replace(' ', 'T')) : null),
};
// SQL 칸의 툴팁 — 앞부분만(긴 문장이 화면을 덮었다). 전체는 눌러서 창으로 본다
const SQL_TIP_MAX = 600;
const fmtInt = (v) => formatNumber(v);
const fmtBytes = (v) => formatBytes(v);
const fmtTime = (iso) => formatDateTime(iso);
/**
 * 벽시계 경과 — `GV$SQL_PLAN_MONITOR` 의 시각 차라 **DATE(1초 해상도)** 다.
 * 1초 미만 오퍼레이션은 0 으로 잡히는데, 활동이 있었다면 `0s` 보다 `<1s` 가 정직하다.
 */
const fmtWall = (sec, ashSamples) => {
    if (sec == null) return '-';
    if (sec === 0 && (ashSamples || 0) > 0) return '<1s';
    return formatSeconds(sec);
};
// 예시 데이터·수집 결과는 대상 Oracle 이 아니라 LiveSQL 백엔드에서 온다.
// 백엔드가 안 떠 있으면 화면이 통째로 비므로, 원인을 구분해서 알려준다.
function describeBackendError(err, t) {
    const status = err && err.response && err.response.status;
    // 502/503/504 는 개발 서버 프록시(또는 리버스 프록시)가 백엔드에 못 닿았을 때 대신 주는 응답이라
    // "응답 오류" 가 아니라 "미도달" 로 안내해야 원인을 바로 찾는다.
    if (!status || status === 502 || status === 503 || status === 504) {
        return t('monitoring:anal.err.backend');
    }
    // 상세 문구는 formatApiError 경유 — errorType 코드가 있으면 번역된다.
    return t('monitoring:anal.err.response', { status: status, message: formatApiError(err, t) });
}

function sqlSnippet(sql, len = 80, t) {
    if (!sql) return t('monitoring:anal.noSqlText');
    const oneLine = String(sql).replace(/\s+/g, ' ').trim();
    return oneLine.length > len ? `${oneLine.slice(0, len)}…` : oneLine;
}

// embedded=true 면 자기 껍데기(overlay/header/탭바/footer)를 그리지 않고 **본문만** 돌려준다.
// 상위 컨테이너(OraclePerfAnalysisModal)가 헤더·탭·연결선택을 소유한다.
const MonitoringAnalysisModal = ({
    isOpen, onClose, connections = [], onOpenInEditor,
    darkMode = false,
    embedded = false, activeTab: activeTabProp, selectedConnId: selectedConnIdProp,
    // 담는 창이 이 본문을 숨겨 둔 동안 — 화면 상태는 그대로 두고 자동 갱신만 멈춘다(2026-10-22)
    paused = false,
    // 세션은 담는 창(OraclePerfAnalysisModal)의 것이다 — 두 탭이 같은 세션을 쓴다.
    // **대상 DB 에 실제로 가는 조회에만** 싣는다. 수집 목록·스냅샷은 로컬 저장소라
    // 세션이 필요 없고, 수집 시작은 수집기가 자기 세션을 따로 쓴다.
    monParams, monRun,
    onRequestTab,           // embedded 일 때 탭을 바꿔 달라고 컨테이너에 요청한다

    liveFilter = null,      // {dimension, value, windowMin} — 실시간 성능에서 고른 Top N 항목
    onClearLiveFilter,
    historyRequest = null,  // { sqlId, seq } — 다른 창(세션 분석)이 「실행 이력」 으로 보낸 SQL(utils/perfJump)
}) => {
    const { t } = useTranslation();
    const [activeTabState, setActiveTab] = useState('active'); // 'active' | 'collections' | 'history'
    const activeTab = embedded ? activeTabProp : activeTabState;
    // embedded 에서 activeTab 은 **prop** 이라 setActiveTab 이 아무 일도 하지 않는다.
    // 「수집 시작」을 눌러도 화면이 안 넘어가던 것이 이것이었다 — 탭 전환은 전부 이 함수로 나간다.
    const goTab = useCallback((tab) => {
        if (embedded) { if (onRequestTab) onRequestTab(tab); return; }
        setActiveTab(tab);
    }, [embedded, onRequestTab]);
    const [selectedConnIdState, setSelectedConnId] = useState(null);
    const selectedConnId = embedded ? selectedConnIdProp : selectedConnIdState;

    // 대상 DB 로 가는 조회에만 붙인다. 혼자 뜰 때(embedded=false)는 세션이 없을 수 있어
    // 연결만 싣는다 — 그러면 백엔드가 예전처럼 일회용 세션으로 답한다.
    const run = useCallback((fn) => (monRun ? monRun(fn) : Promise.resolve().then(fn)), [monRun]);
    const dbTarget = useMemo(
        () => monParams || (selectedConnId ? { connectionId: selectedConnId } : {}),
        [monParams, selectedConnId],
    );
    // 실행계획 · SQL Monitor 리포트 보기 — 실행 중인 쿼리와 실행 이력이 같은 창을 쓴다(2026-10-27)
    const planViewer = useSqlPlanViewer({ run, dbTarget, t });

    // 탭 A: 실행 중인 쿼리
    const [liveRows, setLiveRows] = useState([]);
    // 목록 조건 — **서버가 이 값으로 조회한다.** 받아 온 뒤 거르지 않는다:
    // 예전에는 50건을 받아 놓고 프론트에서 매칭해 "검색했는데 안 나온다" 가 됐다.
    const [liveWindowMin, setLiveWindowMin] = useState(10);
    const [liveOrderBy, setLiveOrderBy] = useState('recent_elapsed');
    const [liveText, setLiveText] = useState('');        // 확정된 검색어(조회에 쓰인다)
    const [liveTextDraft, setLiveTextDraft] = useState(''); // 입력 중인 값 — 타자마다 조회하지 않는다
    const [liveModule, setLiveModule] = useState('');
    // 노드 하나로 좁히기(RAC, 2026-10-14) — null 이면 전체. 고를 수 있는 노드는 거르지 않은 목록에서 모은다
    const [liveInst, setLiveInst] = useState(null);
    const [liveInstOptions, setLiveInstOptions] = useState([]);
    // 실행 상태 리본 — 'all' | 'run' | 'fetch' | 'done' | 'unknown'
    const [liveStateFilter, setLiveStateFilter] = useState('all');
    // SQL 전문 창 — { sqlId, sql } | null
    const [sqlFull, setSqlFull] = useState(null);
    // 실행 이력 — 인스턴스 리본과 차트 x축.
    // 리본은 **클라이언트 필터**다: 커버리지가 인스턴스별로 보장되므로(awrSqlStat 의
    // PARTITION BY) 데이터가 이미 다 와 있다. GV$INSTANCE 를 따로 묻지 않고
    // **응답 행의 distinct** 로 판정한다 — RAC 여도 한 노드에서만 돈 SQL 이면 리본은 무의미하다.
    const [historyInstance, setHistoryInstance] = useState(null);   // null = 전체
    const [historyXAxis, setHistoryXAxis] = useState('time');       // 'time' | 'snap'
    const [liveLoading, setLiveLoading] = useState(false);
    const [liveError, setLiveError] = useState(null);
    // 이번 연결로 [조회] 를 한 번이라도 눌렀는지 — 자동 조회를 하지 않으므로 안내 문구 분기에 사용
    const [liveFetched, setLiveFetched] = useState(false);
    const [demoCollections, setDemoCollections] = useState([]);
    // 예시 데이터 — 세션 분석과 같은 방식(2026-10-03 요청): 체크하면 예시**만**, 끄면 예시는 하나도 섞지 않는다.
    // 예전에는 실행 중 목록 위에 예시 3건을 늘 끼워 넣고 수집 결과에도 섞었다 — 실제 것과 섞이면 무엇이 진짜인지 매번 확인해야 했다.
    // 기본 꺼짐 · 창을 열 때마다 꺼진 채로 · 저장하지 않는다(모니터링 창 공통).
    const [useDemo, setUseDemo] = useState(false);
    useEffect(() => { if (isOpen) setUseDemo(false); }, [isOpen]);
    const [startingRow, setStartingRow] = useState(null); // sql_id — 수집 주기 선택 중인 행
    const [selectedInterval, setSelectedInterval] = useState(60);
    const [starting, setStarting] = useState(false);
    // 수집 시작 시 백엔드가 알려주는 주의사항 (진단/튜닝 팩 설정 등)
    const [warnings, setWarnings] = useState([]);
    // LiveSQL 백엔드 자체에 도달하지 못한 경우 (예시/수집 결과가 통째로 비는 원인)
    const [backendError, setBackendError] = useState(null);
    // 수집 결과 그리드에서 소스(동적뷰)별 컬럼 표시 여부 — 기본 전체 표시
    const [visibleSources, setVisibleSources] = useState(ALL_SOURCES_ON);

    // 공통 — "쿼리보기" 팝오버(어느 탭 것을 보고 있는지) + 탭별 마지막 새로고침 시각
    const [queryViewTab, setQueryViewTab] = useState(null); // null | 'active' | 'collections' | 'history'
    const [queryViewSql, setQueryViewSql] = useState(null);   // null = 불러오는 중
    const [lastRefreshed, setLastRefreshed] = useState({}); // { active: Date, collections: Date, history: Date }
    const stampRefreshed = (tab) => setLastRefreshed(prev => ({ ...prev, [tab]: new Date() }));

    // 탭 C: 실행 이력 (AWR / DBA_HIST_SQLSTAT)
    const [historySqlId, setHistorySqlId] = useState(null);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);
    const [historyIsDemo, setHistoryIsDemo] = useState(false);
    const [historyDays, setHistoryDays] = useState(7);
    // 그래프 종류 — 소요시간(실행당 구성) / 블록 읽기(I/O). 아래 상세 목록이 같은 쪽 컬럼을 칠한다(2026-10-14)
    const [historyMetric, setHistoryMetric] = useState('time');
    // sql_id 직접 입력 조회 + 최근 조회한 5개를 리본(칩)으로 재조회
    const [sqlIdInput, setSqlIdInput] = useState('');
    // SQL 텍스트로 찾기(2026-10-22) — { q, rows, limited, loading, error } | null. 고르면 그 SQL_ID 의 이력을 본다
    const [sqlSearch, setSqlSearch] = useState(null);
    const [recentSqlIds, setRecentSqlIds] = useState(() => {
        try {
            const saved = JSON.parse(localStorage.getItem('livesql_sqlperf_recent_sqlids_v1') || '[]');
            return Array.isArray(saved) ? saved.slice(0, 5) : [];
        } catch { return []; }
    });
    const removeRecentSqlId = (id) => {
        setRecentSqlIds(prev => {
            const next = prev.filter(x => x !== id);
            try { localStorage.setItem('livesql_sqlperf_recent_sqlids_v1', JSON.stringify(next)); } catch { /* 저장 실패는 무시 */ }
            return next;
        });
    };
    const pushRecentSqlId = (id) => {
        setRecentSqlIds(prev => {
            const next = [id, ...prev.filter(x => x !== id)].slice(0, 5);
            try { localStorage.setItem('livesql_sqlperf_recent_sqlids_v1', JSON.stringify(next)); } catch { /* 저장 실패는 무시 */ }
            return next;
        });
    };

    // 탭 B: 수집 결과
    const [collections, setCollections] = useState([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
    const [snapshotDetail, setSnapshotDetail] = useState(null);
    const [collectionsLoading, setCollectionsLoading] = useState(false);
    // **수집**(선택한 SQL 을 주기적으로 찍어 저장)은 모니터링 권한의 것이다 — 조회(실행 중인 쿼리·실행 이력)는 권한 없이도 된다(2026-10-14).
    // 권한이 없으면 서버가 403(MONITORING_FORBIDDEN)을 준다: 오류 배너로 띄우지 않고 「수집은 쓸 수 없다」 고 그 자리에서 말한다
    const [collectDenied, setCollectDenied] = useState(false);
    const isCollectDenied = (err) => !!(err && err.response && err.response.status === 403 && err.response.data && err.response.data.errorType === 'MONITORING_FORBIDDEN');

    const oracleConns = useMemo(
        () => connections.filter(c => c.dbType === 'oracle' || !c.dbType),
        [connections]
    );

    useEffect(() => {
        if (embedded) return;   // 연결 선택은 컨테이너 소유
        if (isOpen && !selectedConnId && oracleConns.length > 0) {
            setSelectedConnId(oracleConns[0].id);
        }
    }, [embedded, isOpen, oracleConns, selectedConnId]);

    // ===== 탭 A: 실행 중인 쿼리 =====
    // 이 조회는 대상 Oracle 에 실제로 접속한다(backend 가 풀을 지연 생성). 모달을 여는 것만으로
    // 접속이 일어나면 감사 로그가 남고, DB 가 내려가 있으면 열 때마다 에러 배너가 뜬다.
    // → 자동 조회하지 않고 사용자가 [조회] 를 누를 때만 실행한다.
    const fetchLiveQueries = useCallback(async () => {
        if (!selectedConnId) { setLiveRows([]); return; }
        setLiveLoading(true);
        setLiveError(null);
        setLiveFetched(true);
        try {
            // 실시간 성능에서 Top N 항목을 골라 들어온 경우 — 그 조건에 해당하는 SQL 만 가져온다.
            // 서버가 바인드로 조건을 걸고 창(windowMin)도 차트와 같은 값을 쓴다
            // (그리드 기본 10분으로 좁히면 120분 창에서 고른 항목이 '없음' 으로 보인다).
            if (liveFilter && liveFilter.dimension && liveFilter.value) {
                const r = await run(() => axios.get(`${DBMS_PERF_API}/ash-sqls`, {
                    params: {
                        ...dbTarget,
                        dimension: liveFilter.dimension,
                        value: liveFilter.value,
                        windowMin: liveFilter.windowMin,
                    },
                }));
                setLiveRows(r.data?.rows || []);
                setLiveLoading(false);
                stampRefreshed('active');
                return;
            }

            const r = await run(() => axios.get(`${PERF_API}/active-queries`, {
                params: {
                    ...dbTarget,
                    windowMin: liveWindowMin,
                    orderBy: liveOrderBy,
                    text: liveText || undefined,
                    module: liveModule || undefined,
                    instId: liveInst ?? undefined,
                },
            }));
            setLiveRows(r.data?.data || []);
            if (liveInst == null) {
                const seen = new Set();
                (r.data?.data || []).forEach(x => { if (x.instMin != null) for (let i = x.instMin; i <= (x.instMax ?? x.instMin); i++) seen.add(i); });
                setLiveInstOptions([...seen].sort((a, b) => a - b));
            }
            stampRefreshed('active');
        } catch (err) {
            setLiveError(formatApiError(err, t));
            setLiveRows([]);
        } finally {
            setLiveLoading(false);
        }
        // dbTarget 이 연결·세션을 품고 있다 — 세션이 정해지면 그 세션으로 다시 묻는다
    }, [run, dbTarget, selectedConnId, liveFilter, liveWindowMin, liveOrderBy, liveText, liveModule, liveInst]);

    // 조건(창·정렬·검색어·모듈·노드)을 바꾸면 **곧바로 다시 묻는다** — 예전에는 고른 뒤 [새로고침] 을 눌러야 적용돼, 정렬·검색이
    // 안 되는 것처럼 보였다(2026-11-03 시험 피드백). 한 번이라도 조회한 뒤에만(열자마자 접속하지 않는 규칙은 그대로)
    const fetchLiveRef = useRef(fetchLiveQueries);
    fetchLiveRef.current = fetchLiveQueries;
    const liveFetchedRef = useRef(false);
    liveFetchedRef.current = liveFetched;
    const liveCondMounted = useRef(false);
    useEffect(() => {
        if (!liveCondMounted.current) { liveCondMounted.current = true; return; }
        if (!liveFetchedRef.current || liveFilter) return;
        fetchLiveRef.current();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- 조건이 바뀔 때만
    }, [liveWindowMin, liveOrderBy, liveText, liveModule, liveInst]);

    // 실시간 성능에서 항목을 고르면 곧바로 결과가 보여야 한다 — 탭만 바뀌고 빈 표가 뜨면
    // 드릴다운이 실패한 것처럼 보인다.
    useEffect(() => {
        if (!isOpen || !liveFilter || !selectedConnId) return;
        fetchLiveQueries();
    }, [isOpen, liveFilter, selectedConnId, fetchLiveQueries]);

    const fetchDemoCollections = useCallback(async () => {
        try {
            const res = await axios.get(`${PERF_API}/collections`);
            setDemoCollections((res.data?.data || []).filter(c => c.is_demo));
            setBackendError(null);
        } catch (err) {
            // 예전에는 조용히 무시했는데, 그러면 예시 3건이 통째로 사라진 채 이유를 알 수 없다
            // (백엔드가 안 떠 있으면 화면이 텅 빈 것처럼 보임). 사유를 표면화한다.
            setDemoCollections([]);
            if (isCollectDenied(err)) setCollectDenied(true);
            else setBackendError(describeBackendError(err, t));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        fetchDemoCollections();
    }, [isOpen, fetchDemoCollections]);

    // 연결을 바꾸면 이전 연결의 조회 결과를 지우고 다시 "미조회" 상태로 되돌린다
    // (바꾸자마자 새 연결로 자동 접속하지 않는다).
    useEffect(() => {
        setLiveRows([]);
        setLiveError(null);
        setLiveFetched(false);
        setStartingRow(null);
    }, [selectedConnId]);

    // 모달을 닫으면 조회 결과를 비운다 — 다시 열었을 때 오래된 "실행 중인 쿼리" 가 남아 있으면
    // 현재 상태로 오인하기 쉽다. (데모/수집 결과는 로컬 데이터라 유지)
    useEffect(() => {
        if (isOpen) return;
        setLiveRows([]);
        setLiveError(null);
        setLiveFetched(false);
    }, [isOpen]);

    const handleStartCollection = async (row) => {
        if (!selectedConnId) return;
        setStarting(true);
        setLiveError(null);
        try {
            const res = await axios.post(`${PERF_API}/collections`, {
                connectionId: selectedConnId,
                sqlId: row.sqlId,
                sqlExecId: row.sqlExecId,
                sqlText: row.sqlText,
                intervalSec: selectedInterval,
            });
            setWarnings(res.data?.warnings || []);
            setStartingRow(null);
            const created = res.data?.data;
            await fetchCollections();
            if (created?.id) setSelectedCollectionId(created.id);
            goTab('collections');
        } catch (err) {
            setLiveError(formatApiError(err, t));
        } finally {
            setStarting(false);
        }
    };

    // ===== 탭 B: 수집 결과 =====
    const fetchCollections = useCallback(async () => {
        setCollectionsLoading(true);
        try {
            const res = await axios.get(`${PERF_API}/collections`, {
                params: selectedConnId ? { connectionId: selectedConnId } : {},
            });
            const rows = res.data?.data || [];
            setCollections(rows);
            setCollectDenied(false);
            setBackendError(null);
            stampRefreshed('collections');
        } catch (err) {
            setCollections([]);
            if (isCollectDenied(err)) setCollectDenied(true);
            else setBackendError(describeBackendError(err, t));
        } finally {
            setCollectionsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedConnId]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'collections') return;
        fetchCollections();
    }, [isOpen, activeTab, fetchCollections]);

    // "실행 중인 쿼리" 탭에서도 각 행이 이미 수집 중인지 판단하려면 collections 목록이 필요하다
    // (LiveSQL 자체 SQLite 조회라 대상 Oracle 에 부담 없음 — 대상 Oracle 접속과는 무관).
    useEffect(() => {
        if (!isOpen || activeTab !== 'active') return;
        fetchCollections();
    }, [isOpen, activeTab, fetchCollections]);

    // 「조회 쿼리 보기」를 열 때마다 서버에서 받는다. 조건(창·정렬·검색)이 바뀌면 SQL 도 달라지므로
    // 캐시하지 않는다 — 지금 이 목록이 무엇으로 나왔는지에 답해야 하는 기능이다.
    useEffect(() => {
        if (!queryViewTab) { setQueryViewSql(null); return; }
        let alive = true;
        setQueryViewSql(null);
        axios.get(`${PERF_API}/source-sql`, {
            params: {
                kind: queryViewTab,
                windowMin: liveWindowMin,
                orderBy: liveOrderBy,
                text: liveText || undefined,
                module: liveModule || undefined,
                instId: liveInst ?? undefined,
            },
        })
            .then(r => { if (alive) setQueryViewSql(r.data?.sql || ''); })
            .catch(err => { if (alive) setQueryViewSql({ error: formatApiError(err, t) }); });
        return () => { alive = false; };
    }, [queryViewTab, liveWindowMin, liveOrderBy, liveText, liveModule, liveInst, t]);

    // keepSelection: 폴링 갱신 — 사용자가 보고 있던 스냅샷 선택을 유지한다.
    const fetchSnapshots = useCallback(async (keepSelection = false) => {
        if (!selectedCollectionId) { setSnapshots([]); setSelectedSnapshotId(null); return; }
        try {
            const res = await axios.get(`${PERF_API}/collections/${selectedCollectionId}/snapshots`);
            const rows = res.data?.data || [];
            setSnapshots(rows);
            setSelectedSnapshotId(prev =>
                (keepSelection && prev && rows.some(r => r.id === prev)) ? prev : (rows[0]?.id || null)
            );
        } catch (_e) {
            setSnapshots([]);
            setSelectedSnapshotId(null);
        }
    }, [selectedCollectionId]);

    useEffect(() => { fetchSnapshots(false); }, [fetchSnapshots]);

    // 수집이 진행 중이면 스냅샷 목록을 주기적으로 갱신 — 새로고침 버튼을 누르지 않아도 새 스냅샷이 보이도록.
    // 수집 주기(최소 60초)보다 촘촘히 돌 필요는 없으므로 30초 간격.
    useEffect(() => {
        if (!isOpen || paused || activeTab !== 'collections') return undefined;
        const coll = collections.find(c => c.id === selectedCollectionId);
        if (!coll || coll.status !== 'running') return undefined;
        // **fetchCollections 를 함께 부른다.** 스냅샷만 갱신하면 자동중단된 뒤에도
        // 화면은 계속 "수집 중" 이고, 폴링도 status 를 보고 도는지라 영원히 멈추지 않는다.
        const timer = setInterval(() => { fetchSnapshots(true); fetchCollections(); }, 30000);
        return () => clearInterval(timer);
    }, [isOpen, paused, activeTab, collections, selectedCollectionId, fetchSnapshots, fetchCollections]);

    useEffect(() => {
        if (!selectedSnapshotId) { setSnapshotDetail(null); return; }
        (async () => {
            try {
                const res = await axios.get(`${PERF_API}/snapshots/${selectedSnapshotId}`);
                setSnapshotDetail(res.data?.data || null);
            } catch (_e) {
                setSnapshotDetail(null);
            }
        })();
    }, [selectedSnapshotId]);

    const handleStopCollection = async (id) => {
        try {
            await axios.delete(`${PERF_API}/collections/${id}`);
            fetchCollections();
        } catch (_e) { /* no-op */ }
    };

    // 수집 결과는 예시 켬/끔에 맞는 것만 — 고른 것이 보이는 쪽에 없으면 첫 번째로
    const shownCollections = useMemo(() => collections.filter(c => !!c.is_demo === useDemo), [collections, useDemo]);
    useEffect(() => {
        if (shownCollections.length > 0 && !shownCollections.some(c => c.id === selectedCollectionId)) {
            setSelectedCollectionId(shownCollections[0].id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shownCollections]);
    const selectedCollection = shownCollections.find(c => c.id === selectedCollectionId);

    // ===== 탭 C: 실행 이력 (AWR) =====
    // DBA_HIST_SQLSTAT 는 스냅샷 구간 델타라 "지난 N일간 이 SQL 이 어떻게 변해왔는지" 를 보여준다.
    // 예시 sql_id 는 백엔드가 더미 이력을 돌려주므로 Oracle 없이도 확인 가능.
    const fetchHistory = useCallback(async (sqlId, days) => {
        if (!sqlId) return;
        setHistoryLoading(true);
        setHistoryError(null);
        try {
            const res = await run(() => axios.get(`${PERF_API}/awr-history`, {
                params: { sqlId, days, ...dbTarget, ...(useDemo ? { demo: 1 } : {}) },
            }));
            setHistoryRows(res.data?.data || []);
            setHistoryIsDemo(!!res.data?.isDemo);
            stampRefreshed('history');
        } catch (err) {
            setHistoryRows([]);
            setHistoryIsDemo(false);
            setHistoryError(formatApiError(err, t));
        } finally {
            setHistoryLoading(false);
        }
    }, [run, dbTarget, useDemo]);

    const handleOpenHistory = (sqlId) => {
        setHistorySqlId(sqlId);
        setSqlIdInput(sqlId);
        pushRecentSqlId(sqlId);
        goTab('history');
        fetchHistory(sqlId, historyDays);
    };
    // 다른 창이 보낸 SQL — 요청마다 한 번(seq)
    const historyReqSeq = historyRequest ? historyRequest.seq : 0;
    useEffect(() => {
        if (!historyRequest || !historyRequest.sqlId) return;
        setSqlSearch(null);
        handleOpenHistory(historyRequest.sqlId);
    }, [historyReqSeq]); // eslint-disable-line react-hooks/exhaustive-deps -- 새 요청에만

    // sql_id 직접 입력 조회 — 실행 중인 쿼리 탭에서 클릭해 넘어오지 않아도 임의 sql_id 를 바로 조회.
    // SQL_ID 모양(13자리 영숫자)이 아니면 **SQL 텍스트로 찾는다**(2026-10-22) — AWR 에 남은 문장에서 후보를 받아 사람이 고른다
    const searchSqlText = useCallback(async (q, days) => {
        setSqlSearch({ q, rows: [], loading: true });
        try {
            const res = await run(() => axios.get(`${PERF_API}/awr-sql-search`, {
                params: { q, days, ...dbTarget, ...(useDemo ? { demo: 1 } : {}) },
            }));
            setSqlSearch({ q, rows: res.data?.data || [], limited: !!res.data?.limited, loading: false });
        } catch (err) {
            setSqlSearch({ q, rows: [], loading: false, error: formatApiError(err, t) });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [run, dbTarget, useDemo]);
    const handleLookupSqlId = () => {
        const v = sqlIdInput.trim();
        if (!v) return;
        if (!SQL_ID_SHAPE.test(v)) {
            if (v.length < SQL_SEARCH_MIN) { setSqlSearch({ q: v, rows: [], loading: false, error: t('monitoring:anal.sqlSearch.tooShort', { n: SQL_SEARCH_MIN }) }); return; }
            searchSqlText(v, historyDays);
            return;
        }
        setSqlSearch(null);
        setHistorySqlId(v);
        pushRecentSqlId(v);
        fetchHistory(v, historyDays);
    };

    const visiblePlanColumns = useMemo(
        () => getPlanColumns(t).filter(c => !c.source || visibleSources[c.source]),
        [visibleSources]
    );

    const toggleSource = (key) => setVisibleSources(prev => ({ ...prev, [key]: !prev[key] }));

    // 컬럼 1개의 셀 내용 — 컬럼 정의가 소스 토글로 늘었다 줄었다 하므로 key 기반으로 분기한다.
    const renderPlanCell = (col, pl) => {
        const numCls = col.numeric ? 'analysis-num' : undefined;
        switch (col.key) {
            case 'id':
                return (
                    <td
                        key={col.key}
                        className="analysis-mono"
                        title={pl.extra && pl.extra.predicates && pl.extra.predicates.length > 0
                            ? pl.extra.predicates.join('\n') : undefined}
                    >
                        {pl.extra && pl.extra.hasAsterisk ? '*' : ''}{pl.plan_line_id}
                    </td>
                );
            case 'operation':
                // 실행계획 트리 깊이만큼 들여쓰기 — 부모/자식 관계를 눈으로 따라갈 수 있게
                return (
                    <td key={col.key} className="analysis-op-cell">
                        <span
                            className="analysis-op-indent"
                            style={{ paddingLeft: `${(pl.extra?.depth || 0) * 14}px` }}
                        >
                            {(pl.extra?.depth || 0) > 0 && <i className="analysis-op-branch" aria-hidden="true" />}
                            {pl.operation}
                        </span>
                    </td>
                );
            case 'object':        return <td key={col.key}>{pl.object_name || '-'}</td>;
            // ASH 샘플 1건 ≈ 활성세션 1초. 병렬이면 DOP 배수로 커지는 것이 **정상**이다.
            case 'dbTime':        return <td key={col.key} className={numCls}>{fmtSec(pl.ash_samples)}</td>;
            // 벽시계 경과 — DATE(1초 해상도)라 1초 미만은 0 으로 잡힌다
            case 'elapsed':       return <td key={col.key} className={numCls}>{fmtWall(pl.active_sec, pl.ash_samples)}</td>;
            case 'rows':          return <td key={col.key} className={numCls}>{fmtInt(pl.rows_out)}</td>;
            case 'readBytes':     return <td key={col.key} className={numCls}>{fmtBytes(pl.physical_read_bytes)}</td>;
            case 'physicalReads': return <td key={col.key} className={numCls}>{fmtInt(pl.physical_reads)}</td>;
            case 'cacheReads':    return <td key={col.key} className={numCls}>{fmtInt(pl.cache_reads)}</td>;
            case 'waitEvent':     return <td key={col.key}>{pl.wait_event || '-'}</td>;
            case 'memory':        return <td key={col.key} className={numCls}>{fmtBytes(pl.memory_used)}</td>;
            case 'temp':          return <td key={col.key} className={numCls}>{fmtBytes(pl.temp_used)}</td>;
            default:              return <td key={col.key} className={numCls}>-</td>;
        }
    };

    /**
     * **문장 전체** 수치 — GV$SQL_MONITOR 의 문장 한 행(QC).
     *
     * SQL Monitor 리포트 헤더와 **같은 값**이라, 아래 plan line 그리드가 맞는지 대조할 기준이 된다.
     * 값이 없으면(팩·권한 없음) 줄 자체를 그리지 않는다 — 0 으로 채우면 "읽기가 없었다" 로 읽힌다.
     */
    const stmtTotals = useMemo(() => {
        const d = snapshotDetail;
        if (!d) return null;
        const items = [
            { key: 'elapsed', label: t('monitoring:anal.col.elapsed'), v: d.stmt_elapsed_sec, fmt: fmtSec },
            { key: 'cpu', label: 'CPU', v: d.stmt_cpu_sec, fmt: fmtSec },
            { key: 'buffers', label: t('monitoring:anal.col.buffers'), v: d.stmt_buffer_gets, fmt: fmtInt },
            { key: 'reads', label: t('monitoring:anal.col.physReads'), v: d.stmt_disk_reads, fmt: fmtInt },
            { key: 'bytes', label: t('monitoring:anal.stmt.readBytes'), v: d.stmt_read_bytes, fmt: fmtBytes },
            { key: 'rows', label: t('monitoring:anal.col.rows'), v: d.stmt_rows, fmt: fmtInt },
        ].filter(x => x.v != null).map(x => ({ key: x.key, label: x.label, value: x.fmt(x.v) }));
        if (!items.length) return null;
        return { items, dop: d.px_dop != null ? d.px_dop : null, status: d.exec_status || null };
    }, [snapshotDetail, t]);

    // 조건절 정보 (요구사항 3) — DISPLAY_CURSOR 의 Predicate Information 을 plan line 별로 모아 표시.
    const predicateRows = useMemo(() => {
        const lines = snapshotDetail?.plan_lines || [];
        return lines
            .filter(pl => pl.extra && Array.isArray(pl.extra.predicates) && pl.extra.predicates.length > 0)
            .map(pl => ({ id: pl.plan_line_id, texts: pl.extra.predicates }));
    }, [snapshotDetail]);

    // 소스 토글의 on/off 와 별개로 — 이 스냅샷에서 실제로 그 소스가 값을 채웠는지.
    // (예: SQL 이 ACTIVE 상태가 아니었으면 ASH 대기이벤트가 하나도 없어 'ash' 소스는 켜져 있어도 빈 컬럼)
    const sourceHasData = useMemo(() => {
        const lines = snapshotDetail?.plan_lines || [];
        // **각 소스의 고유 값으로 판정한다.** 예전에는 xplan 을 `object_name` 유무로 봤는데,
        // 그 이름은 이제 plan monitor 에서 오므로 xplan 이 아무 값도 안 채워도 "있음" 으로 읽혔다.
        return {
            xplan: lines.some(pl => pl.cache_reads != null || pl.xplan_elapsed_sec != null),
            monitor: lines.some(pl => pl.active_sec != null || pl.rows_out != null
                || pl.physical_reads != null || pl.physical_read_bytes != null),
            ash: lines.some(pl => pl.wait_event || pl.ash_samples != null),
            workarea: lines.some(pl => pl.memory_used != null || pl.temp_used != null),
        };
    }, [snapshotDetail]);

    // 값이 비어 보이는 이유를 설명하는 안내 — ALLSTATS 통계는 대상 SQL 이
    // GATHER_PLAN_STATISTICS 로 실행됐거나 statistics_level=ALL 일 때만 채워진다.
    const statsSourceNote = useMemo(() => {
        const lines = snapshotDetail?.plan_lines || [];
        if (lines.length === 0) return null;
        const sources = new Set(lines.map(pl => pl.extra && pl.extra.statsSource).filter(Boolean));
        if (sources.size === 0 || sources.has('xplan') || sources.has('demo')) return null;
        if (sources.has('plan_monitor')) {
            return t('monitoring:anal.err.noBuffers');
        }
        return t('monitoring:anal.err.noStats');
    }, [snapshotDetail]);

    const handleOpenInEditor = () => {
        if (!onOpenInEditor) return;
        onOpenInEditor({
            sqlText: selectedCollection?.sql_text || '',
            planText: snapshotDetail?.plan_cursor_text || '',
            monitorReport: snapshotDetail?.sql_monitor_report || null,
            label: selectedCollection ? t('monitoring:anal.shortWithId', { sqlId: selectedCollection.sql_id }) : t('monitoring:anal.short'),
        });
        onClose();
    };

    // 실행 중인 쿼리 목록 = 데모(항상 상단) + 라이브 조회 결과
    // 필터로 좁혀 본 결과에 예시 3건을 섞으면 "이게 조건에 맞는 SQL 인가?" 를 알 수 없다.
    const liveRowsAll = useDemo
        ? demoCollections.map(c => ({
            sqlId: c.sql_id, sqlExecId: c.sql_exec_id, sqlText: c.sql_text,
            isDemo: true, collectionId: c.id, module: t('monitoring:anal.sampleParen'), sessionCount: '-', lastSeen: null,
        }))
        : liveRows;
    // 실행 중인 것이 먼저, 같은 상태 안에서는 고른 정렬 기준(큰 것부터). 실시간 성능에서 넘어온 목록은 서버의 부하 순을 그대로 둔다
    const liveStateCounts = liveRowsAll.reduce((a, r) => { const st = liveStateOf(r); a[st] = (a[st] || 0) + 1; return a; }, {});
    const combinedLiveRows = liveRowsAll
        .map((r, i) => ({ r, i, st: liveStateOf(r) }))
        .filter(x => liveStateFilter === 'all' || x.st === liveStateFilter)
        .sort((a, b) => {
            const d = LIVE_STATE_RANK[a.st] - LIVE_STATE_RANK[b.st];
            if (d) return d;
            const val = !liveFilter && LIVE_ORDER_VALUE[liveOrderBy];
            if (val) {
                const va = val(a.r); const vb = val(b.r);
                const na = va == null || va === '-' || !Number.isFinite(Number(va)); const nb = vb == null || vb === '-' || !Number.isFinite(Number(vb));
                if (na !== nb) return na ? 1 : -1;
                if (!na && Number(vb) !== Number(va)) return Number(vb) - Number(va);
            }
            return a.i - b.i;
        })
        .map(x => x.r);

    // 리본에 세울 인스턴스 — 응답에 실제로 있는 것만
    const historyInstances = useMemo(
        () => [...new Set(historyRows.map(h => h.instanceNumber).filter(v => v != null))].sort((a, b) => a - b),
        [historyRows]
    );
    const historyViewRows = useMemo(
        () => (historyInstance == null ? historyRows : historyRows.filter(h => h.instanceNumber === historyInstance)),
        [historyRows, historyInstance]
    );

    // 그래프 한 장에는 **노드 하나만** 그린다 — RAC 2노드를 한 선에 올리면 같은 x 에 점이 둘이라 선이 스스로를 가로지른다.
    // 노드가 여럿이고 「전체」 면 노드마다 한 장씩(같은 가로축), 노드를 고르면 그 노드만(2026-10-14 — 예전에는 첫 노드 하나만 그렸다)
    const historyChartSets = useMemo(() => {
        const insts = historyInstance != null ? [historyInstance] : (historyInstances.length > 0 ? historyInstances : [null]);
        return insts.map(inst => ({
            inst,
            data: historyRows
                .filter(h => inst == null || h.instanceNumber === inst)
                // 행은 snap_id DESC 로 온다(최신이 위). 차트만 오름차순으로 뒤집는다 — 표의 정렬을 따라가면 시간이 거꾸로 흐른다
                .slice().sort((a, b) => (a.snapId || 0) - (b.snapId || 0))
                // ISO 를 쓴다 — 'YYYY-MM-DD HH:MM' 은 브라우저마다 파싱이 갈려 어떤 곳에서는 시간축이 아니라 글자 축이 된다
                .map(h => ({ t: historyXAxis === 'time' ? (h.beginTimeIso || h.beginTime || String(h.snapId)) : String(h.snapId), ...historyChartPoint(h) })),
        }));
    }, [historyRows, historyInstance, historyInstances, historyXAxis]);
    const historyChartData = historyChartSets.reduce((a, x) => (x.data.length > a.length ? x.data : a), []);
    // 노드마다 그린 그래프는 가로축을 맞춘다(시간축일 때) — 노드마다 스냅샷 범위가 조금씩 달라 같은 x 가 다른 시각이 되지 않게
    const historyDomain = useMemo(() => {
        if (historyXAxis !== 'time' || historyChartSets.length < 2) return undefined;
        const ms = historyChartSets.flatMap(x => x.data.map(d => new Date(d.t).getTime())).filter(Number.isFinite);
        return ms.length ? [Math.min(...ms), Math.max(...ms)] : undefined;
    }, [historyChartSets, historyXAxis]);

    // 계열 **키는 영문 고정**이고 번역은 name 에만 붙인다(키를 번역하면 차트가 조용히 깨진다).
    // 두 그래프 모두 왼쪽은 **실행당 평균**, 오른쪽 보조 축은 실행 횟수다 — 구간 합계를 그리면 실행이 많은 구간이 무거워 보일 뿐이다.
    // 완료 0회 구간은 실행당이 없어(null) 선·면이 끊긴다 — 0 으로 메우지 않는다(「순식간에 끝났다」 로 읽힌다)
    const historyPanel = useMemo(() => (historyMetric === 'io' ? {
        chartType: 'line',
        xField: 't',
        yFields: ['bufferGetsPerExec', 'diskReadsPerExec', 'executions'],
        yFieldsConfig: {
            bufferGetsPerExec: { name: t('monitoring:anal.chart.bufferGetsPerExec'), color: '#3b82f6' },
            diskReadsPerExec: { name: t('monitoring:anal.chart.diskReadsPerExec'), color: '#10b981' },
            executions: { name: t('monitoring:anal.chart.executions'), axis: 'right', color: '#9ca3af' },
        },
        // 블록 수는 자릿수가 커서 천 단위로 끊어 적는다(1,234,567)
        yAxis: { thousands: true },
        yAxisRight: { thousands: true },
    } : {
        chartType: 'stackedarea',
        xField: 't',
        yFields: [...HISTORY_TIME_PARTS, 'executions'],
        yFieldsConfig: {
            ...Object.fromEntries(HISTORY_TIME_PARTS.map(k => [k, { name: t(`monitoring:anal.chart.part.${k}`), color: HISTORY_PART_COLORS[k] }])),
            executions: { name: t('monitoring:anal.chart.executions'), axis: 'right', color: '#6b7280' },
        },
        yAxisRight: { thousands: true },
    }), [t, historyMetric]);

    // 수집 결과 왼쪽 목록 — 위/아래 화살표로 스냅샷을 옮긴다(Home/End 는 처음·끝). 옮긴 항목에 포커스도 따라가야 연달아 누를 수 있다
    const onSnapshotListKey = (e) => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key) || snapshots.length === 0) return;
        e.preventDefault();
        const cur = snapshots.findIndex(x => x.id === selectedSnapshotId);
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? snapshots.length - 1
            : Math.min(snapshots.length - 1, Math.max(0, (cur < 0 ? -1 : cur) + (e.key === 'ArrowDown' ? 1 : -1)));
        const id = snapshots[next].id;
        setSelectedSnapshotId(id);
        const el = e.currentTarget.querySelector(`[data-snap-id="${id}"]`);
        if (el) { el.focus(); el.scrollIntoView({ block: 'nearest' }); }
    };
    const liveColumns = getLiveColumns(t);
    // 지금 정렬 기준인 컬럼은 칸을 옅게 칠한다(실시간 성능에서 넘어온 목록은 서버가 부하 순으로 준다)
    const liveCellCls = (order) => `analysis-num${!liveFilter && liveOrderBy === order ? ' is-sorted' : ''}`;
    const historyColumns = getHistoryColumns(t);

    if (!isOpen) return null;

    // "쿼리보기" 팝오버 — 탭 목록이 실제로 어떻게 채워지는지 투명하게 보여준다.
    // **사본을 들고 있지 않다**: 서버가 지금 도는 SQL 을 그대로 내려 준다(`/source-sql`).
    // 예전에는 이 파일에 사본 문자열이 있었고 이미 한 번 어긋났다 — 투명성이 목적인 기능에서
    // 사본이 어긋나면 거짓을 보여 주는 것이라 없느니만 못하다.
    const queryViewPopover = queryViewTab && (
        <QueryViewDialog
            title={t('monitoring:anal.viewQueryTitle', { tab: t(`monitoring:anal.tab.${queryViewTab === 'active' ? 'running' : queryViewTab}`) })}
            sql={typeof queryViewSql === 'string' ? queryViewSql : null}
            error={queryViewSql && queryViewSql.error}
            loadingText={t('monitoring:anal.viewQueryLoading')}
            note={t('monitoring:anal.viewQueryNote')}
            onClose={() => setQueryViewTab(null)}
        />
    );

    // SQL 전문 창 — 실행 중인 쿼리의 SQL 칸을 누르면(툴팁은 앞부분만)
    const sqlFullPopover = sqlFull && (
        <QueryViewDialog
            title={t('monitoring:anal.sqlFullTitle', { sqlId: sqlFull.sqlId })}
            sql={sqlFull.sql}
            onClose={() => setSqlFull(null)}
        />
    );

    // ── 본문 ──────────────────────────────────────────────────────────────
    // embedded 모드에서는 이 조각만 반환한다 (컨테이너가 헤더·탭·연결선택을 소유).
    const body = (
        <>
            {queryViewPopover}
            {sqlFullPopover}
            {planViewer.dialog}
            <div className="analysis-demo-bar">
                <label className="analysis-demo-toggle">
                    <input type="checkbox" checked={useDemo} onChange={(e) => setUseDemo(e.target.checked)} />
                    {t('monitoring:anal.demoData')}
                </label>
            </div>
            {/* LiveSQL 백엔드 자체에 못 닿은 경우 — 예시 3건과 수집 결과가 함께 비므로
                "아무것도 안 보인다" 로 보인다. 두 탭 공통으로 사유와 재시도를 노출. */}
            {backendError && (
                <div className="analysis-backend-error">
                    <FiAlertTriangle size={14} />
                    <span>{backendError}</span>
                    <button
                        type="button"
                        onClick={() => {
                            fetchDemoCollections();
                            if (activeTab === 'collections') fetchCollections();
                        }}
                    >
                        {t('monitoring:anal.retry')}
                    </button>
                </div>
            )}

            <div className="analysis-modal-body analysis-modal-body-flex">
                {activeTab === 'active' && (
                    // 위(도구 줄·리본·표 머리)는 서고 표 본문만 스크롤된다(2026-11-03 시험 피드백)
                    <div className="analysis-fade-swap analysis-active-pane">
                        <div className="analysis-toolbar">
                            <button
                                className={`analysis-btn ${liveFetched ? 'analysis-btn-secondary' : 'analysis-btn-primary'}`}
                                onClick={fetchLiveQueries}
                                disabled={liveLoading || !selectedConnId}
                            >
                                <FiRefreshCw size={12} className={liveLoading ? 'analysis-spin' : ''} />
                                {liveFetched ? t('monitoring:anal.act.refresh') : t('monitoring:anal.act.run')}
                            </button>
                            {liveFilter ? (
                                <span className="analysis-filter-chip">
                                    <FiFilter size={12} />
                                    <b>{getDimensionLabel(t)[liveFilter.dimension] || liveFilter.dimension}</b>
                                    <code>{liveFilter.value}</code>
                                    <span className="analysis-filter-window">{t('monitoring:anal.lastMinutes', { n: liveFilter.windowMin })}</span>
                                    {onClearLiveFilter && (
                                        <button type="button" onClick={onClearLiveFilter} aria-label={t('monitoring:anal.act.clearFilter')}>
                                            <FiX size={11} />
                                        </button>
                                    )}
                                </span>
                            ) : (
                                <span className="analysis-toolbar-note">
                                    <FiInfo size={12} />
                                    {liveFetched
                                        ? t('monitoring:anal.ashNote', { n: (liveFilter && liveFilter.windowMin) || liveWindowMin })
                                        : t('monitoring:anal.pressRunHint')}
                                </span>
                            )}
                            {/* 조건은 **서버로 간다.** 받아 온 뒤 거르지 않는다 —
                                예전에는 50건을 받아 놓고 프론트에서 매칭해 1000자 뒤의 토큰을 못 찾았다.
                                실시간 성능에서 넘어온 경우는 그쪽 조건이 이긴다(창까지 그대로 쓴다). */}
                            {!liveFilter && (
                                <>
                                    <select
                                        className="analysis-select analysis-select-inline"
                                        value={liveWindowMin}
                                        onChange={(e) => setLiveWindowMin(Number(e.target.value))}
                                        title={t('monitoring:anal.act.windowHint')}
                                    >
                                        {[10, 30, 60, 120].map(n => (
                                            <option key={n} value={n}>{t('monitoring:anal.lastMinutes', { n })}</option>
                                        ))}
                                    </select>
                                    <select
                                        className="analysis-select analysis-select-inline"
                                        value={liveOrderBy}
                                        onChange={(e) => setLiveOrderBy(e.target.value)}
                                        title={t('monitoring:anal.act.orderHint')}
                                    >
                                        {LIVE_ORDERS.map(o => <option key={o} value={o}>{t(`monitoring:anal.orderBy.${o}`)}</option>)}
                                        <option value="sessions">{t('monitoring:anal.order.sessions')}</option>
                                        <option value="last_seen">{t('monitoring:anal.order.lastSeen')}</option>
                                        <option value="first_seen">{t('monitoring:anal.order.firstSeen')}</option>
                                    </select>
                                    <input
                                        className="analysis-input analysis-input-inline"
                                        value={liveTextDraft}
                                        placeholder={t('monitoring:anal.act.searchPlaceholder')}
                                        title={t('monitoring:anal.act.searchHint')}
                                        onChange={(e) => setLiveTextDraft(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') setLiveText(liveTextDraft.trim()); }}
                                        onBlur={() => setLiveText(liveTextDraft.trim())}
                                    />
                                    {/* 노드 — 리본(2026-11-03 시험 피드백, 예전에는 선택 상자). 누르면 그 노드로 다시 묻는다 */}
                                    {(liveInstOptions.length > 1 || liveInst != null) && (
                                        <span className="analysis-ribbon" role="group" aria-label={t('monitoring:anal.instFilterHint')} title={t('monitoring:anal.instFilterHint')}>
                                            <button type="button" aria-pressed={liveInst == null} className={`analysis-ribbon-chip${liveInst == null ? ' on' : ''}`} onClick={() => setLiveInst(null)}>
                                                {t('monitoring:anal.instAll')}
                                            </button>
                                            {[...new Set([...liveInstOptions, ...(liveInst != null ? [liveInst] : [])])].sort((a, b) => a - b).map(n => (
                                                <button key={n} type="button" aria-pressed={liveInst === n} className={`analysis-ribbon-chip${liveInst === n ? ' on' : ''}`}
                                                    onClick={() => setLiveInst(liveInst === n ? null : n)}>
                                                    {t('monitoring:anal.instN', { n })}
                                                </button>
                                            ))}
                                        </span>
                                    )}
                                    {liveModule && (
                                        <span className="analysis-filter-chip">
                                            <FiFilter size={12} />
                                            <code>{liveModule}</code>
                                            <button type="button" onClick={() => setLiveModule('')} aria-label={t('monitoring:anal.act.clearFilter')}>
                                                <FiX size={11} />
                                            </button>
                                        </span>
                                    )}
                                </>
                            )}
                            <div className="analysis-tabs-spacer" />
                            {lastRefreshed.active && (
                                <span className="analysis-refreshed-at">{t('monitoring:anal.refreshedAt', { time: fmtTime(lastRefreshed.active.toISOString()) })}</span>
                            )}
                            <QueryViewButton onClick={() => setQueryViewTab('active')} />
                        </div>
                        {liveError && <div className="analysis-error">{liveError}</div>}
                        {/* 실행 상태 리본 — 기본은 전체(실행 중인 것이 먼저), 누르면 그 상태만 */}
                        {liveRowsAll.length > 0 && (
                            <div className="analysis-ribbon analysis-state-ribbon" role="group" aria-label={t('monitoring:anal.col.state')}>
                                {['all', 'run', 'fetch', 'done', 'unknown'].map(st => {
                                    const n = st === 'all' ? liveRowsAll.length : (liveStateCounts[st] || 0);
                                    if (st !== 'all' && st !== 'run' && !n) return null;
                                    const label = st === 'all' ? t('monitoring:anal.stateAll')
                                        : t(`monitoring:anal.state.${{ run: 'running', fetch: 'fetchWait', done: 'ended', unknown: 'unknown' }[st]}`);
                                    return (
                                        <button key={st} type="button" aria-pressed={liveStateFilter === st}
                                            className={`analysis-ribbon-chip${liveStateFilter === st ? ' on' : ''}${st !== 'all' ? ` analysis-state-chip-${st}` : ''}`}
                                            onClick={() => setLiveStateFilter(liveStateFilter === st ? 'all' : st)}>
                                            {label} <span className="analysis-ribbon-count">{n}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <div className="analysis-table-container">
                            <table className="analysis-table">
                                <thead>
                                    <tr>
                                        {liveColumns.map(c => (c.order && !liveFilter ? (
                                            <th
                                                key={c.key}
                                                className={`analysis-th-sortable${c.num ? ' analysis-num' : ''}${liveOrderBy === c.order ? ' is-sorted' : ''}`}
                                                title={c.hint || undefined}
                                                aria-sort={liveOrderBy === c.order ? 'descending' : 'none'}
                                            >
                                                <button type="button" onClick={() => setLiveOrderBy(c.order)}>
                                                    {c.label}{liveOrderBy === c.order && <span aria-hidden="true"> {'\u25BC'}</span>}
                                                </button>
                                            </th>
                                        ) : <th key={c.key} className={c.num ? 'analysis-num' : undefined} title={c.hint || undefined}>{c.label}</th>))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {combinedLiveRows.map(row => (
                                        <React.Fragment key={`${row.isDemo ? 'demo' : 'live'}-${row.sqlId}`}>
                                            <tr className={row.isDemo ? 'analysis-row-demo' : ''}>
                                                <td className="analysis-mono">
                                                    {row.isDemo && <span className="analysis-badge-demo">{t('monitoring:anal.sample')}</span>}
                                                    {row.sqlId}
                                                </td>
                                                <td className={`analysis-sql-cell${row.sqlText ? ' is-clickable' : ''}`}
                                                    title={row.sqlText ? `${row.sqlText.length > SQL_TIP_MAX ? `${row.sqlText.slice(0, SQL_TIP_MAX)}…` : row.sqlText}\n\n${t('monitoring:anal.sqlFullHint')}` : ''}
                                                    onClick={row.sqlText ? () => setSqlFull({ sqlId: row.sqlId, sql: row.sqlText }) : undefined}>
                                                    {sqlSnippet(row.sqlText, 80, t)}
                                                </td>
                                                <td>
                                                    {/* 판정은 서버가 gv$session 으로 한다. `null` 은 "못 물어봤다" 이지
                                                        "안 돌고 있다" 가 아니다 — 둘을 같은 말로 보이면 안 된다. */}
                                                    {/* 페치 대기(2026-10-27) — DB 는 쉬고 있지만 실행은 열려 있다(클라이언트가 다음 FETCH 를 아직 부르지 않았다) */}
                                                    {row.isDemo ? '-' : (() => {
                                                        const st = row.isRunning === true ? 'run' : row.fetchWait ? 'fetch' : row.isRunning === false ? 'done' : 'unknown';
                                                        const key = { run: 'running', fetch: 'fetchWait', done: 'ended', unknown: 'unknown' }[st];
                                                        return (
                                                            <span className={`analysis-state-chip analysis-state-${st}`} title={t(`monitoring:anal.state.${key}Hint`)}>
                                                                {t(`monitoring:anal.state.${key}`)}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                <td className="analysis-nowrap">{instLabel(row)}</td>
                                                {/* ASH 는 1초마다 찍으므로 샘플 수 ≈ 초 — 「초」 로 보이되 머리글 설명이 그 사실을 말한다 */}
                                                <td className={liveCellCls('recent_elapsed')}>{fmtSecOrDash(row.recentElapsedSec)}</td>
                                                <td className={liveCellCls('samples')}>{fmtSecOrDash(row.samples)}</td>
                                                <td className={liveCellCls('avg_elapsed')}>{fmtSecOrDash(row.avgElapsedSec)}</td>
                                                <td className={liveCellCls('exec_count')}>{row.execCount != null ? row.execCount : '-'}</td>
                                                <td className={liveCellCls('cpu')}>{fmtSecOrDash(row.cpuSamples)}</td>
                                                <td className={liveCellCls('lock')}>{fmtSecOrDash(row.lockSamples)}</td>
                                                <td className={liveCellCls('sessions')}>{row.sessionCount}</td>
                                                <td>
                                                    {row.module && !row.isDemo ? (
                                                        <button
                                                            type="button"
                                                            className="analysis-link-cell"
                                                            title={t('monitoring:anal.act.filterByModule')}
                                                            onClick={() => setLiveModule(row.module)}
                                                        >
                                                            {row.module}
                                                        </button>
                                                    ) : (row.module || '-')}
                                                </td>
                                                <td className="analysis-nowrap">{fmtTime(row.lastSeen)}</td>
                                                <td className="analysis-row-actions">
                                                    {/* 작은 아이콘 단추(2026-10-27 요청 — 글자 단추가 줄을 넓혔다). 뜻은 툴팁이 말한다 */}
                                                    {row.isDemo ? (
                                                        <button className="analysis-icon-btn" title={t('monitoring:anal.viewResult')} aria-label={t('monitoring:anal.viewResult')}
                                                            onClick={() => { setSelectedCollectionId(row.collectionId); goTab('collections'); }}>
                                                            <FiEye size={13} />
                                                        </button>
                                                    ) : (() => {
                                                        // 이미 이 sql_id 로 수집이 진행 중이면 「수집 시작」 대신 결과로 바로 간다
                                                        const running = collections.find(c => c.status === 'running' && c.sql_id === row.sqlId);
                                                        if (running) {
                                                            return (
                                                                <button className="analysis-icon-btn is-primary" title={t('monitoring:anal.alreadyCollectingView')} aria-label={t('monitoring:anal.viewResult')}
                                                                    onClick={() => { setSelectedCollectionId(running.id); goTab('collections'); }}>
                                                                    <FiEye size={13} />
                                                                </button>
                                                            );
                                                        }
                                                        // 이미 끝난 실행은 수집해도 빈 스냅샷만 쌓인다 — 막고 이유를 말한다. 페치 대기는 끝난 것이 아니다(수집할 수 있다).
                                                        // `null`(판정 못 함)은 막지 않는다: 모른다고 기능을 잠그면 팩·권한이 없는 환경에서 수집을 아예 시작할 수 없다
                                                        const ended = row.isRunning === false && !row.fetchWait;
                                                        const tip = ended ? t('monitoring:anal.state.endedHint') : collectDenied ? t('monitoring:anal.collectDenied') : t('monitoring:anal.startCollect');
                                                        return (
                                                            <button className={`analysis-icon-btn is-primary${startingRow === row.sqlId ? ' on' : ''}`} title={tip} aria-label={t('monitoring:anal.startCollect')}
                                                                disabled={ended || collectDenied}
                                                                onClick={() => setStartingRow(startingRow === row.sqlId ? null : row.sqlId)}>
                                                                <FiPlay size={13} />
                                                            </button>
                                                        );
                                                    })()}
                                                    {!row.isDemo && (
                                                        <>
                                                            <button className="analysis-icon-btn" title={t('monitoring:planView.planHint')} aria-label={t('monitoring:planView.plan')}
                                                                onClick={() => planViewer.openPlan({ sqlId: row.sqlId, instId: row.instId })}>
                                                                <FiLayers size={13} />
                                                            </button>
                                                            <button className="analysis-icon-btn" title={t('monitoring:planView.monHint')} aria-label={t('monitoring:planView.mon')}
                                                                onClick={() => planViewer.openMonitor({ sqlId: row.sqlId, instId: row.instId, sqlExecId: row.sqlExecId, sqlExecStart: row.sqlExecStart })}>
                                                                <FiMonitor size={13} />
                                                            </button>
                                                        </>
                                                    )}
                                                    {/* AWR(DBA_HIST_SQLSTAT) 기반 과거 실행 추이 — 지금 돌고 있지 않아도 조회 가능 */}
                                                    <button className="analysis-icon-btn" title={t('monitoring:anal.awrHistory')} aria-label={t('monitoring:anal.viewHistory')}
                                                        onClick={() => handleOpenHistory(row.sqlId)}>
                                                        <FiTrendingUp size={13} />
                                                    </button>
                                                </td>
                                            </tr>
                                            {startingRow === row.sqlId && !row.isDemo && (
                                                <tr className="analysis-row-inline-panel">
                                                    <td colSpan={liveColumns.length}>
                                                        <div className="analysis-interval-panel analysis-fade-swap">
                                                            <span>{t('monitoring:anal.col.intervalColon')}</span>
                                                            {getIntervalOptions(t).map(opt => (
                                                                <label key={opt.value} className="analysis-radio">
                                                                    <input
                                                                        type="radio"
                                                                        name="interval"
                                                                        checked={selectedInterval === opt.value}
                                                                        onChange={() => setSelectedInterval(opt.value)}
                                                                    />
                                                                    {opt.label}
                                                                </label>
                                                            ))}
                                                            <button
                                                                className="analysis-btn analysis-btn-primary analysis-btn-small"
                                                                disabled={starting}
                                                                onClick={() => handleStartCollection(row)}
                                                            >
                                                                {t('monitoring:anal.start')}
                                                            </button>
                                                            <button
                                                                className="analysis-btn analysis-btn-small"
                                                                onClick={() => setStartingRow(null)}
                                                            >
                                                                {t('monitoring:anal.cancel')}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    {!liveFetched && !liveLoading && (
                                        <tr>
                                            <td colSpan={liveColumns.length} className="analysis-empty-cell">
                                                {selectedConnId
                                                    ? t('monitoring:anal.pressRunAbove')
                                                    : t('monitoring:anal.pickOracleFirst')}
                                            </td>
                                        </tr>
                                    )}
                                    {liveFetched && combinedLiveRows.length === 0 && !liveLoading && (
                                        <tr><td colSpan={liveColumns.length} className="analysis-empty-cell">{t('monitoring:anal.noQueries')}</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeTab === 'collections' && (
                    <div className="analysis-fade-swap analysis-collections-layout">
                        {collectDenied && <div className="analysis-empty-note" role="note">{t('monitoring:anal.collectDenied')}</div>}
                        <div className="analysis-toolbar">
                            <select
                                className="analysis-select analysis-select-inline"
                                value={selectedCollectionId || ''}
                                onChange={(e) => setSelectedCollectionId(Number(e.target.value))}
                            >
                                {shownCollections.length === 0 && <option value="">{t('monitoring:anal.noData')}</option>}
                                {shownCollections.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {c.is_demo ? `[${t('monitoring:anal.sample')}] ` : ''}{c.sql_id}{c.inst_id != null ? ` · ${t('monitoring:anal.instN', { n: c.inst_id })}` : ''} — {c.status === 'running' ? t('monitoring:anal.collecting') : t('monitoring:anal.stopped')} ({(c.interval_sec < 60 ? t('monitoring:anal.everySeconds', { n: c.interval_sec }) : t('monitoring:anal.everyMinutes', { n: c.interval_sec / 60 }))})
                                    </option>
                                ))}
                            </select>
                            <button className="analysis-btn analysis-btn-secondary" onClick={fetchCollections} disabled={collectionsLoading}>
                                <FiRefreshCw size={12} className={collectionsLoading ? 'analysis-spin' : ''} />
                            </button>
                            {selectedCollection && !selectedCollection.is_demo && selectedCollection.status === 'running' && (
                                <button className="analysis-btn analysis-btn-secondary" onClick={() => handleStopCollection(selectedCollection.id)}>
                                    {t('monitoring:anal.stopCollect')}
                                </button>
                            )}
                            {/* 자동중단이 생긴 뒤로는 "왜 멈췄나" 가 곧바로 첫 질문이 된다.
                                예전엔 사유 넷이 전부 '중지됨' 하나로 뭉개져 답할 수가 없었다. */}
                            {selectedCollection && !selectedCollection.is_demo
                                && selectedCollection.status !== 'running' && selectedCollection.stop_reason && (
                                <span
                                    className="analysis-stop-reason"
                                    title={t(`monitoring:anal.stopReasonHint.${selectedCollection.stop_reason}`, '')}
                                >
                                    {t(`monitoring:anal.stopReason.${selectedCollection.stop_reason}`,
                                        selectedCollection.stop_reason)}
                                </span>
                            )}
                            <div className="analysis-tabs-spacer" />
                            {lastRefreshed.collections && (
                                <span className="analysis-refreshed-at">{t('monitoring:anal.refreshedAt', { time: fmtTime(lastRefreshed.collections.toISOString()) })}</span>
                            )}
                            <QueryViewButton onClick={() => setQueryViewTab('collections')} />
                            <button
                                className="analysis-btn analysis-btn-primary analysis-btn-small"
                                onClick={handleOpenInEditor}
                                disabled={!selectedCollection}
                            >
                                <FiExternalLink size={12} /> {t('monitoring:anal.openInEditor')}
                            </button>
                        </div>

                        {warnings.length > 0 && (
                            <div className="analysis-warning">
                                <FiInfo size={12} />
                                <ul>
                                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                                </ul>
                            </div>
                        )}

                        {selectedCollection && (
                            <div className="analysis-sql-preview" title={selectedCollection.sql_text}>
                                {sqlSnippet(selectedCollection.sql_text, 160, t)}
                            </div>
                        )}

                        <div className="analysis-snapshot-layout">
                            <div className="analysis-snapshot-list" onKeyDown={onSnapshotListKey}>
                                {snapshots.map(s => (
                                    <button
                                        key={s.id}
                                        data-snap-id={s.id}
                                        className={`analysis-snapshot-item ${s.id === selectedSnapshotId ? 'active' : ''}`}
                                        onClick={() => setSelectedSnapshotId(s.id)}
                                    >
                                        {fmtTime(s.collected_at)}
                                        {!!s.is_parallel && <span className="analysis-badge-parallel">{t('monitoring:anal.col.parallel')}</span>}
                                        {s.exec_phase === 'fetch_wait' && <span className="analysis-badge-fetch" title={t('monitoring:anal.state.fetchWaitSnapHint')}>{t('monitoring:anal.state.fetchWait')}</span>}
                                    </button>
                                ))}
                                {snapshots.length === 0 && <div className="analysis-empty-cell">{t('monitoring:anal.noSnapshot')}</div>}
                                {/* 수집이 실패하고 있으면 그 까닭(2026-10-27 — 「수집 시작을 했는데 스냅샷이 없다」) */}
                                {selectedCollection && selectedCollection.last_error && (
                                    <div className="analysis-error analysis-collect-error" title={selectedCollection.last_error}>
                                        {t('monitoring:anal.lastError', { message: selectedCollection.last_error })}
                                    </div>
                                )}
                            </div>

                            <div className="analysis-snapshot-detail analysis-fade-swap">
                                {snapshotDetail ? (
                                    <>
                                        {!!snapshotDetail.is_parallel && snapshotDetail.parallel_summary?.length > 0 && (
                                            <div className="analysis-parallel-box">
                                                <strong>{t('monitoring:anal.parallelInfo')}</strong>
                                                <table className="analysis-table analysis-table-compact">
                                                    <thead>
                                                        <tr><th>{t('monitoring:anal.col.pxServer')}</th><th>{t('monitoring:anal.col.group')}</th><th>Degree</th><th>{t('monitoring:anal.col.status')}</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {snapshotDetail.parallel_summary.map((p, idx) => (
                                                            <tr key={idx}>
                                                                <td>{p.serverName}</td>
                                                                <td>{p.serverGroup}</td>
                                                                <td>{p.degree}/{p.reqDegree}</td>
                                                                <td>{p.status}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {statsSourceNote && (
                                            <div className="analysis-stats-note">
                                                <FiInfo size={12} /> {statsSourceNote}
                                            </div>
                                        )}

                                        {/* 그리드 컬럼의 출처(동적뷰)별 토글 — 어떤 뷰에서 온 정보인지 보여주면서
                                            필요 없는 소스는 숨겨 폭을 줄인다. ID/Operation 은 항상 표시. */}
                                        <div className="analysis-source-toggles">
                                            <span className="analysis-source-label">{t('monitoring:anal.col.source')}</span>
                                            {getPlanSources(t).map(src => {
                                                const hasData = sourceHasData[src.key];
                                                return (
                                                    <button
                                                        key={src.key}
                                                        type="button"
                                                        className={`analysis-source-chip ${visibleSources[src.key] ? 'on' : 'off'} ${hasData ? '' : 'unused'}`}
                                                        title={`${src.title}\n${visibleSources[src.key] ? t('monitoring:anal.clickToHide') : t('monitoring:anal.clickToShow')}${hasData ? '' : `\n${t('monitoring:anal.sourceUnused')}`}`}
                                                        aria-pressed={visibleSources[src.key]}
                                                        onClick={() => toggleSource(src.key)}
                                                    >
                                                        {visibleSources[src.key] ? <FiEye size={11} /> : <FiEyeOff size={11} />}
                                                        {src.label}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        {/* **문장 전체** — GV$SQL_MONITOR 의 문장 한 행이고 SQL Monitor 리포트
                                            헤더와 같은 값이다. 아래 표는 plan line 별 값이라 **합계가 달라도 정상**이다.
                                            리포트와 그리드를 대조할 기준점이 없던 것을 메운다(2026-09-21). */}
                                        {/* 옛 스냅샷은 지표 정의가 섞여 있었다(소스 폴백). 값을 지우지 않고
                                            **그 사실을 말한다** — 지우면 왜 사라졌는지 알 수가 없다. */}
                                        {snapshotDetail && !snapshotDetail.metrics_basis && (
                                            <div className="analysis-legacy-note">
                                                {t('monitoring:anal.legacySnapshot')}
                                            </div>
                                        )}
                                        {snapshotDetail && snapshotDetail.ash_covered_from
                                            && snapshotDetail.sql_exec_start
                                            && ashTruncatedSec(snapshotDetail.sql_exec_start, snapshotDetail.ash_covered_from) > ASH_TRUNC_TOLERANCE_SEC && (
                                            <div className="analysis-legacy-note">
                                                {t('monitoring:anal.ashTruncated', { from: snapshotDetail.ash_covered_from })}
                                            </div>
                                        )}
                                        {stmtTotals && (
                                            <div className="analysis-stmt-total">
                                                <span className="analysis-stmt-label">{t('monitoring:anal.stmt.title')}</span>
                                                {stmtTotals.items.map(it => (
                                                    <span key={it.key} className="analysis-stmt-item">
                                                        <span className="analysis-stmt-k">{it.label}</span>
                                                        <span className="analysis-stmt-v">{it.value}</span>
                                                    </span>
                                                ))}
                                                {stmtTotals.dop != null && (
                                                    <span className="analysis-stmt-badge" title={t('monitoring:anal.stmt.dopHint')}>
                                                        DOP {stmtTotals.dop}
                                                    </span>
                                                )}
                                                {stmtTotals.status && (
                                                    <span className="analysis-stmt-badge">{stmtTotals.status}</span>
                                                )}
                                                <span className="analysis-stmt-note">{t('monitoring:anal.stmt.note')}</span>
                                            </div>
                                        )}

                                        <div className="analysis-plan-table-container">
                                            <table className="analysis-table analysis-table-compact">
                                                <thead>
                                                    <tr>
                                                        {visiblePlanColumns.map(col => (
                                                            <th
                                                                key={col.key}
                                                                className={col.numeric ? 'analysis-num' : undefined}
                                                                // 값의 성격(활성세션 기준·최종실행 등)을 헤더가 말한다
                                                                title={col.hint || undefined}
                                                            >
                                                                {col.label}
                                                                {col.hint && <i className="analysis-col-hint" aria-hidden="true">?</i>}
                                                            </th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(snapshotDetail.plan_lines || []).map(pl => (
                                                        <tr key={pl.id}>
                                                            {visiblePlanColumns.map(col => renderPlanCell(col, pl))}
                                                        </tr>
                                                    ))}
                                                    {(!snapshotDetail.plan_lines || snapshotDetail.plan_lines.length === 0) && (
                                                        <tr>
                                                            <td colSpan={visiblePlanColumns.length} className="analysis-empty-cell">
                                                                {t('monitoring:anal.noPlanData')}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* 오브젝트별 읽기량 — 버퍼 읽기(블록 수)와 물리 읽기(요청 횟수)를 한 줄에. 「비용」 이라 부르지 않는다
                                            (옵티마이저 COST 가 아니라 읽은 양이다). 기본은 접어 둔다 — 계획 표가 먼저 보여야 한다(2026-10-14) */}
                                        {(snapshotDetail.plan_lines || []).some(pl => pl.cache_reads != null || pl.physical_reads != null) && (
                                            <details className="analysis-report-details">
                                                <summary>{t('monitoring:anal.objChart.title')}</summary>
                                                <ObjectReadsList lines={snapshotDetail.plan_lines} />
                                            </details>
                                        )}

                                        {/* 바인드 변수 — SQL Monitor 의 BINDS_XML(이번 실행에 쓰인 값),
                                            없으면 GV$SQL_BIND_CAPTURE 로 폴백해 수집된 값 */}
                                        {(snapshotDetail.binds || []).length > 0 && (
                                            <details className="analysis-report-details" open>
                                                <summary>
                                                    {t('monitoring:anal.bindVars', { n: snapshotDetail.binds.length })}
                                                    <span className="analysis-binds-source">
                                                        {snapshotDetail.binds[0]?.source || ''}
                                                    </span>
                                                </summary>
                                                <table className="analysis-table analysis-table-compact analysis-binds-table">
                                                    <thead>
                                                        <tr>
                                                            <th>#</th>
                                                            <th>{t('monitoring:anal.col.name')}</th>
                                                            <th>{t('monitoring:anal.col.type')}</th>
                                                            <th>{t('monitoring:anal.col.value')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {snapshotDetail.binds.map((b, idx) => (
                                                            <tr key={`${b.name || idx}-${idx}`}>
                                                                <td>{b.position ?? idx + 1}</td>
                                                                <td className="analysis-mono">{b.name || '-'}</td>
                                                                <td>{b.dataType || '-'}</td>
                                                                <td className="analysis-mono analysis-bind-value" title={b.value == null ? 'NULL' : String(b.value)}>
                                                                    {b.value == null
                                                                        ? <em className="analysis-bind-null">NULL</em>
                                                                        : String(b.value)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </details>
                                        )}

                                        {predicateRows.length > 0 && (
                                            <details className="analysis-report-details">
                                                <summary>{t('monitoring:anal.predicates')}</summary>
                                                <div className="analysis-predicate-list">
                                                    {predicateRows.map(p => (
                                                        <div key={p.id} className="analysis-predicate-item">
                                                            <span className="analysis-predicate-id">{p.id}</span>
                                                            <span className="analysis-predicate-text">{p.texts.join('\n')}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                        )}

                                        {snapshotDetail.sql_monitor_report && (
                                            <details className="analysis-report-details">
                                                <summary>{t('monitoring:anal.monitorReport')}</summary>
                                                <pre className="analysis-report-pre">{snapshotDetail.sql_monitor_report}</pre>
                                            </details>
                                        )}
                                    </>
                                ) : (
                                    <div className="analysis-empty-cell">{t('monitoring:anal.pickSnapshot')}</div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'history' && (
                    <div className="analysis-fade-swap analysis-collections-layout">
                        <div className="analysis-toolbar">
                            <div className="analysis-sqlid-input">
                                <FiSearch size={12} />
                                <input
                                    type="text"
                                    className="analysis-mono"
                                    placeholder={t('monitoring:anal.sqlSearch.placeholder')}
                                    title={t('monitoring:anal.sqlSearch.hint', { n: SQL_SEARCH_MIN })}
                                    value={sqlIdInput}
                                    onChange={(e) => setSqlIdInput(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') handleLookupSqlId(); }}
                                />
                            </div>
                            <label className="analysis-history-days">
                                {t('monitoring:anal.period')}
                                <select
                                    className="analysis-select analysis-select-inline"
                                    value={historyDays}
                                    onChange={(e) => {
                                        const d = Number(e.target.value);
                                        setHistoryDays(d);
                                        if (historySqlId) fetchHistory(historySqlId, d);
                                    }}
                                >
                                    <option value={1}>{t('monitoring:anal.range.day1')}</option>
                                    <option value={7}>{t('monitoring:anal.range.day7')}</option>
                                    <option value={30}>{t('monitoring:anal.range.day30')}</option>
                                </select>
                            </label>
                            <button
                                className="analysis-btn analysis-btn-primary analysis-btn-small"
                                onClick={handleLookupSqlId}
                                disabled={!sqlIdInput.trim() || historyLoading}
                            >
                                <FiRefreshCw size={12} className={historyLoading ? 'analysis-spin' : ''} />{t('monitoring:anal.act.lookup')}
                            </button>
                            <div className="analysis-tabs-spacer" />
                            {lastRefreshed.history && (
                                <span className="analysis-refreshed-at">{t('monitoring:anal.refreshedAt', { time: fmtTime(lastRefreshed.history.toISOString()) })}</span>
                            )}
                            {historySqlId && !historyIsDemo && (
                                <button className="analysis-btn analysis-btn-small analysis-btn-secondary" title={t('monitoring:planView.planHintHist')}
                                    onClick={() => planViewer.openPlan({ sqlId: historySqlId })}>
                                    <FiLayers size={11} /> {t('monitoring:planView.plan')}
                                </button>
                            )}
                            <QueryViewButton onClick={() => setQueryViewTab('history')} />
                            <span className="analysis-toolbar-note">
                                <FiInfo size={12} /> {t('monitoring:anal.awrSource')}
                                {historyIsDemo && <span className="analysis-badge-demo">{t('monitoring:anal.sample')}</span>}
                            </span>
                        </div>

                        {/* SQL 텍스트로 찾은 후보 — 줄을 누르면 그 SQL_ID 의 실행 이력을 본다 */}
                        {sqlSearch && (
                            <div className="analysis-sqlsearch">
                                <div className="analysis-sqlsearch-head">
                                    <span>
                                        {sqlSearch.loading ? t('monitoring:anal.sqlSearch.loading', { q: sqlSearch.q })
                                            : sqlSearch.error ? sqlSearch.error
                                            : sqlSearch.rows.length ? t('monitoring:anal.sqlSearch.found', { q: sqlSearch.q, n: sqlSearch.rows.length })
                                            : t('monitoring:anal.sqlSearch.none', { q: sqlSearch.q })}
                                        {sqlSearch.limited && ` ${t('monitoring:anal.sqlSearch.limited', { n: sqlSearch.rows.length })}`}
                                    </span>
                                    <button type="button" className="analysis-recent-chip-x" onClick={() => setSqlSearch(null)} title={t('monitoring:anal.close')} aria-label={t('monitoring:anal.close')}><FiX size={12} /></button>
                                </div>
                                {sqlSearch.rows.length > 0 && (
                                    <div className="analysis-sqlsearch-list">
                                        <table className="analysis-table">
                                            <thead>
                                                <tr>
                                                    <th>SQL_ID</th>
                                                    <th className="analysis-num">{t('monitoring:anal.sqlSearch.execs')}</th>
                                                    <th className="analysis-num">{t('monitoring:anal.sqlSearch.elapsed')}</th>
                                                    <th>{t('monitoring:anal.sqlSearch.last')}</th>
                                                    <th>{t('monitoring:anal.sqlSearch.text')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sqlSearch.rows.map(r => (
                                                    <tr key={r.sqlId} className={`analysis-sqlsearch-row${r.sqlId === historySqlId ? ' active' : ''}`}
                                                        onClick={() => handleOpenHistory(r.sqlId)} title={t('monitoring:anal.sqlSearch.pick')}>
                                                        <td className="analysis-mono">{r.sqlId}</td>
                                                        <td className="analysis-num">{r.executions == null ? '-' : formatNumber(r.executions)}</td>
                                                        <td className="analysis-num">{r.elapsedSec == null ? '-' : formatNumber(r.elapsedSec)}</td>
                                                        <td>{r.lastTime || '-'}</td>
                                                        <td className="analysis-sqlsearch-text analysis-mono" title={r.sqlHead}>{r.sqlHead}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}

                        {recentSqlIds.length > 0 && (
                            <div className="analysis-recent-sqlids">
                                <span className="analysis-recent-label">{t('monitoring:anal.recentQueries')}</span>
                                {recentSqlIds.map(id => (
                                    <span key={id} className={`analysis-recent-chip analysis-recent-chip-removable ${id === historySqlId ? 'active' : ''}`}>
                                        <button
                                            type="button"
                                            className="analysis-recent-chip-main"
                                            onClick={() => { setSqlIdInput(id); setHistorySqlId(id); fetchHistory(id, historyDays); }}
                                        >
                                            {id}
                                        </button>
                                        {/* 리본에서만 지운다 — 조회 중인 결과는 그대로 둔다 */}
                                        <button
                                            type="button"
                                            className="analysis-recent-chip-x"
                                            title={t('monitoring:anal.removeRecent')}
                                            aria-label={t('monitoring:anal.removeRecentOf', { id })}
                                            onClick={() => removeRecentSqlId(id)}
                                        >
                                            <FiX size={10} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {historyError && <div className="analysis-error">{historyError}</div>}

                        {/* 인스턴스 리본 — RAC 에서 한 노드만 보고 싶을 때.
                            노드가 하나면 세우지 않는다(누를 것이 없는 리본은 소음이다). */}
                        {historyInstances.length > 1 && (
                            <div className="analysis-recent-sqlids">
                                <span className="analysis-recent-label">{t('monitoring:anal.instance')}</span>
                                <button
                                    type="button"
                                    className={`analysis-recent-chip ${historyInstance == null ? 'active' : ''}`}
                                    onClick={() => setHistoryInstance(null)}
                                >
                                    {t('monitoring:anal.allInstances')}
                                </button>
                                {historyInstances.map(n => (
                                    <button
                                        key={n}
                                        type="button"
                                        className={`analysis-recent-chip ${historyInstance === n ? 'active' : ''}`}
                                        onClick={() => setHistoryInstance(n)}
                                    >
                                        {n}
                                    </button>
                                ))}
                            </div>
                        )}

                        {historyChartData.length > 1 && (
                            <div className="analysis-history-chart">
                                <div className="analysis-history-chart-head">
                                    <span className="analysis-toolbar-note">
                                        <FiInfo size={12} />
                                        {historyChartSets.length > 1 ? t('monitoring:anal.chart.perInstance') : t('monitoring:anal.chart.note')}
                                    </span>
                                    <div className="analysis-tabs-spacer" />
                                    <div className="analysis-seg" role="tablist" aria-label={t('monitoring:anal.chart.metric')}>
                                        {['time', 'io'].map(m => (
                                            <button
                                                key={m}
                                                type="button"
                                                role="tab"
                                                aria-selected={historyMetric === m}
                                                className={historyMetric === m ? 'active' : ''}
                                                onClick={() => setHistoryMetric(m)}
                                            >
                                                {t(`monitoring:anal.chart.metric_${m}`)}
                                            </button>
                                        ))}
                                    </div>
                                    <button
                                        type="button"
                                        className="analysis-btn analysis-btn-small analysis-btn-secondary"
                                        onClick={() => setHistoryXAxis(historyXAxis === 'time' ? 'snap' : 'time')}
                                    >
                                        {t(`monitoring:anal.chart.x.${historyXAxis === 'time' ? 'time' : 'snap'}`)}
                                    </button>
                                </div>
                                <div className="analysis-chart-note">{t(`monitoring:anal.chart.metricNote_${historyMetric}`)}</div>
                                {/* 블록 수는 천 단위로 끊어 적어 자릿수가 길다 — 왼쪽 축 폭을 넓힌다 */}
                                {historyChartSets.map(({ inst, data }) => (
                                    <div key={inst ?? 'all'} className="analysis-history-inst">
                                        {historyChartSets.length > 1 && <div className="analysis-history-inst-label">{t('monitoring:anal.instN', { n: inst })}</div>}
                                        <PanelChart panel={historyPanel} data={data} height={historyChartSets.length > 1 ? 170 : 220} isDarkMode={darkMode}
                                            timeDomain={historyDomain} forcedLeftAxisWidth={historyMetric === 'io' ? 76 : 58} />
                                    </div>
                                ))}
                            </div>
                        )}

                        <details className="analysis-report-details analysis-history-details" open={historyChartData.length <= 1 || undefined}>
                        <summary>{t('monitoring:anal.historyDetail', { n: historyViewRows.length })}</summary>
                        <div className="analysis-table-container">
                            <table className="analysis-table analysis-table-compact">
                                <thead>
                                    {/* 2단 머리글 — 이 표의 수치는 대부분 **구간 합계(Δ)** 인데
                                        그게 안 보여서 실행당 값으로 오해를 샀다. */}
                                    <tr className="analysis-group-head">
                                        {HISTORY_GROUPS.map(g => {
                                            const n = historyColumns.filter(c => c.group === g).length;
                                            return (
                                                <th
                                                    key={g}
                                                    colSpan={n}
                                                    className={`analysis-group-${g}`}
                                                    title={t(`monitoring:anal.group.${g}Hint`)}
                                                >
                                                    {t(`monitoring:anal.group.${g}`)}
                                                </th>
                                            );
                                        })}
                                    </tr>
                                    <tr>
                                        {historyColumns.map(c => (
                                            <th key={c.key} className={[c.fmt ? 'analysis-num' : '', c.rel === historyMetric ? 'is-related' : ''].filter(Boolean).join(' ') || undefined}>{c.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyViewRows.map((h, idx) => (
                                        <tr key={`${h.snapId}-${h.instanceNumber}-${idx}`}>
                                            {historyColumns.map(c => {
                                                const v = h[c.key];
                                                // 완료 0회 구간은 실행당을 **쟴 수가 없다**. 빈 칸만 두면
                                                // 고장으로 읽히므로 그 사실을 배지로 말한다.
                                                if (c.key === 'executions' && v === 0) {
                                                    return (
                                                        <td key={c.key} className="analysis-num">
                                                            <span className="analysis-zeroexec-badge" title={t('monitoring:anal.zeroExecHint')}>
                                                                {t('monitoring:anal.zeroExec')}
                                                            </span>
                                                        </td>
                                                    );
                                                }
                                                const text = v == null ? '-'
                                                    : c.fmt === 'sec' ? fmtSec(v)
                                                        : c.fmt === 'int' ? fmtInt(v)
                                                            : String(v);
                                                // 계획 해시를 누르면 그 계획(AWR)을 본다(2026-10-27)
                                                if (c.key === 'planHashValue' && v != null && !historyIsDemo) {
                                                    return (
                                                        <td key={c.key} className="analysis-mono">
                                                            <button type="button" className="analysis-link-cell" title={t('monitoring:planView.hashHint')}
                                                                onClick={() => planViewer.openPlan({ sqlId: historySqlId, planHash: v, source: 'awr' })}>
                                                                {text}
                                                            </button>
                                                        </td>
                                                    );
                                                }
                                                return (
                                                    <td
                                                        key={c.key}
                                                        className={[c.fmt ? 'analysis-num' : '', c.mono ? 'analysis-mono' : '', c.rel === historyMetric ? 'is-related' : ''].filter(Boolean).join(' ')}
                                                        title={v == null && c.group === 'perExec' ? t('monitoring:anal.zeroExecHint') : undefined}
                                                    >
                                                        {text}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                    {historyViewRows.length === 0 && !historyLoading && (
                                        <tr>
                                            <td colSpan={historyColumns.length} className="analysis-empty-cell">
                                                {!historySqlId
                                                    ? t('monitoring:anal.pressHistory')
                                                    : historyError
                                                        ? t('monitoring:anal.err.awr')
                                                        : t('monitoring:anal.err.noAwrHistory', { days: historyDays })}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        </details>

                        <div className="analysis-stats-note">
                            <FiInfo size={12} />
                            {t('monitoring:anal.awrLicense')}
                        </div>
                    </div>
                )}
            </div>
        </>
    );

    if (embedded) return body;

    return (
        <AppModal windowed
            open
            onClose={onClose}
            icon={<FiBarChart2 size={14} />}
            title={t('monitoring:anal.title')}
            overlayClassName="analysis-modal-overlay"
            className="analysis-modal analysis-modal-wide"
            headerClassName="analysis-modal-header"
            closeClassName="analysis-modal-close"
        >

                <div className="analysis-tabs">
                    <button
                        className={`analysis-tab ${activeTab === 'active' ? 'active' : ''}`}
                        onClick={() => goTab('active')}
                    >
                        <FiCpu size={13} /> {t('monitoring:anal.tab.running')}
                    </button>
                    <button
                        className={`analysis-tab ${activeTab === 'collections' ? 'active' : ''}`}
                        onClick={() => goTab('collections')}
                    >
                        <FiClock size={13} /> {t('monitoring:anal.tab.collections')}
                    </button>
                    <button
                        className={`analysis-tab ${activeTab === 'history' ? 'active' : ''}`}
                        onClick={() => goTab('history')}
                    >
                        <FiTrendingUp size={13} /> {t('monitoring:anal.tab.history')}
                    </button>
                    <div className="analysis-tabs-spacer" />
                    <select
                        className="analysis-select analysis-select-inline"
                        value={selectedConnId || ''}
                        onChange={(e) => setSelectedConnId(e.target.value)}
                    >
                        {oracleConns.length === 0 && <option value="">{t('monitoring:anal.noOracle')}</option>}
                        {oracleConns.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </select>
                </div>

                {body}

                <div className="analysis-modal-footer">
                    <button className="app-btn analysis-btn analysis-btn-secondary" onClick={onClose}>
                        {t('monitoring:anal.close')}
                    </button>
                </div>
        </AppModal>
    );
};

export default MonitoringAnalysisModal;
